import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  keyframes,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import HandshakeIcon from "@mui/icons-material/Handshake";
import BusinessIcon from "@mui/icons-material/Business";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LanguageIcon from "@mui/icons-material/Language";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.haymestasarim.com/api";

const ENDPOINTS = {
  dashboard: "/bayilik/my-dashboard",
};

const MOBILE_BREAKPOINT = 930;

const BRAND = {
  blue: "#0B4D8B",
  blue2: "#1565C0",
  orange: "#F58220",
  dark: "#0F172A",
  ink: "#1E293B",
  muted: "#64748B",
  bg: "#F8FAFC",
  softBlue: "#EAF4FF",
  softOrange: "#FFF3E6",
  softGreen: "#ECFDF5",
  softRed: "#FEF2F2",
  softYellow: "#FFFBEB",
  danger: "#EF4444",
  border: "rgba(15, 23, 42, 0.10)",
  border2: "rgba(148, 163, 184, 0.24)",
};

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shine = keyframes`
  0% {
    background-position: -180% 0;
  }
  100% {
    background-position: 180% 0;
  }
`;

const floatSoft = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const TEXT = {
  tr: {
    pageTitle: "Bayilik Sistemi",
    pageSubtitle:
      "Referans satışlarınızı, müşteri kayıtlarını, sözleşme tarihlerini ve hak edişlerinizi tek ekrandan profesyonel şekilde takip edin.",
    refresh: "Yenile",
    refreshing: "Yenileniyor",
    exportCsv: "CSV İndir",
    lastUpdate: "Son güncelleme",
    never: "Henüz yok",
    totalRecords: "Toplam İşlem",
    activeContracts: "Aktif Sözleşme",
    upcomingRenewals: "Yaklaşan Yenileme",
    totalSalesAmount: "Toplam Satış",
    infoCommission: "Hak Ediş",
    searchPlaceholder: "Firma, referans, ürün, müşteri veya not ara...",
    recordsTitle: "Referans / Satış Bilgi Kayıtları",
    recordsSubtitle: "Admin panelinden girilen satış ve referans kayıtları.",
    reportsTitle: "Raporlar",
    reportsSubtitle:
      "En çok satış yapılan müşterileri ve en çok satılan ürün/paketleri takip edin.",
    customerReport: "En Çok Satış Yapılan Müşteriler",
    customerReportSub: "Hangi firma / hesap en çok aldı?",
    productReport: "Ürün / Paket Satış Raporu",
    productReportSub: "En çok hangi ürün veya paket satıldı?",
    noReport: "Rapor için yeterli veri yok.",
    noData: "Henüz kayıt yok.",
    noDataText:
      "Admin panelinden referans / satış işlemi girildiğinde burada görünecek.",
    company: "Firma",
    customerAccount: "Satış Yapılan Hesap",
    partner: "Referans / Satışçı",
    packageName: "Satılan Ürün / Paket",
    processType: "İşlem Tipi",
    saleAmount: "Satış Tutarı",
    commissionStep: "Hak Ediş",
    rate: "Oran",
    commissionAmount: "Hak Ediş",
    startDate: "Başlangıç",
    endDate: "Bitiş",
    agreementDate: "Anlaşma Tarihi",
    remainingDay: "Kalan Süre",
    note: "Not",
    noNote: "Not girilmemiş",
    sale: "Satış",
    demo: "Demo",
    renewal: "Yenileme",
    setup: "Kurulum",
    dateMissing: "Tarih yok",
    dayLeft: "gün kaldı",
    dayPassed: "gün geçti",
    errorTitle: "Veriler alınamadı",
    errorText:
      "Bağlantı veya backend endpoint kontrol edilmeli. Sayfayı yenileyip tekrar deneyebilirsiniz.",
    loadingTitle: "Bayilik bilgileri yükleniyor",
    loadingText:
      "Referans kayıtları, sözleşme tarihleri ve hak edişler hazırlanıyor.",
    rows: "kayıt",
    quantity: "Adet",
    sales: "Satışlar",
    salesListed: "kayıt listeleniyor",
    customerMissing: "Müşteri bilgisi yok",
    productMissing: "Ürün bilgisi yok",
  },
  en: {
    pageTitle: "Partner Sales System",
    pageSubtitle:
      "Track referral sales, customer records, contract dates and commissions professionally.",
    refresh: "Refresh",
    refreshing: "Refreshing",
    exportCsv: "Download CSV",
    lastUpdate: "Last update",
    never: "Never",
    totalRecords: "Total Records",
    activeContracts: "Active Contracts",
    upcomingRenewals: "Upcoming Renewals",
    totalSalesAmount: "Total Sales",
    infoCommission: "Commission",
    searchPlaceholder: "Search company, referral, product, customer or note...",
    recordsTitle: "Referral / Sales Records",
    recordsSubtitle: "Sales and referral records entered by admin.",
    reportsTitle: "Reports",
    reportsSubtitle: "Track top customers and top sold products/packages.",
    customerReport: "Top Customers",
    customerReportSub: "Which company / account bought the most?",
    productReport: "Product / Package Report",
    productReportSub: "Which product or package sold the most?",
    noReport: "Not enough data for report.",
    noData: "No records yet.",
    noDataText: "Records will appear here when admin enters them.",
    company: "Company",
    customerAccount: "Sold To",
    partner: "Referral / Seller",
    packageName: "Sold Product / Package",
    processType: "Process Type",
    saleAmount: "Sale Amount",
    commissionStep: "Commission",
    rate: "Rate",
    commissionAmount: "Commission",
    startDate: "Start",
    endDate: "End",
    agreementDate: "Agreement Date",
    remainingDay: "Time Left",
    note: "Note",
    noNote: "No note",
    sale: "Sale",
    demo: "Demo",
    renewal: "Renewal",
    setup: "Setup",
    dateMissing: "No date",
    dayLeft: "days left",
    dayPassed: "days passed",
    errorTitle: "Could not load data",
    errorText: "Check connection or backend endpoint and refresh the page.",
    loadingTitle: "Loading partner records",
    loadingText: "Referral records, contract dates and commissions are loading.",
    rows: "records",
    quantity: "Qty",
    sales: "Sales",
    salesListed: "records listed",
    customerMissing: "No customer",
    productMissing: "No product",
  },
  de: {
    pageTitle: "Partner-Vertriebssystem",
    pageSubtitle:
      "Empfehlungsverkäufe, Kunden, Vertragsdaten und Provisionen professionell verfolgen.",
    refresh: "Aktualisieren",
    refreshing: "Aktualisiert",
    exportCsv: "CSV herunterladen",
    lastUpdate: "Letzte Aktualisierung",
    never: "Noch nie",
    totalRecords: "Gesamt",
    activeContracts: "Aktive Verträge",
    upcomingRenewals: "Anstehende Verlängerungen",
    totalSalesAmount: "Gesamtverkauf",
    infoCommission: "Provision",
    searchPlaceholder: "Firma, Empfehlung, Produkt, Kunde oder Notiz suchen...",
    recordsTitle: "Empfehlungs-/Verkaufsdaten",
    recordsSubtitle: "Vom Admin eingetragene Verkaufs- und Empfehlungsdaten.",
    reportsTitle: "Berichte",
    reportsSubtitle: "Top-Kunden und meistverkaufte Produkte/Pakete verfolgen.",
    customerReport: "Top Kunden",
    customerReportSub: "Welche Firma / welches Konto kaufte am meisten?",
    productReport: "Produkt-/Paketbericht",
    productReportSub: "Welches Produkt oder Paket wurde am meisten verkauft?",
    noReport: "Nicht genug Daten für Bericht.",
    noData: "Noch keine Datensätze.",
    noDataText: "Datensätze erscheinen, wenn der Admin sie einträgt.",
    company: "Firma",
    customerAccount: "Verkauft an",
    partner: "Empfehlung / Verkäufer",
    packageName: "Verkauftes Produkt / Paket",
    processType: "Vorgang",
    saleAmount: "Verkaufsbetrag",
    commissionStep: "Provision",
    rate: "Satz",
    commissionAmount: "Provision",
    startDate: "Start",
    endDate: "Ende",
    agreementDate: "Vereinbarungsdatum",
    remainingDay: "Restzeit",
    note: "Notiz",
    noNote: "Keine Notiz",
    sale: "Verkauf",
    demo: "Demo",
    renewal: "Verlängerung",
    setup: "Einrichtung",
    dateMissing: "Kein Datum",
    dayLeft: "Tage übrig",
    dayPassed: "Tage vorbei",
    errorTitle: "Daten konnten nicht geladen werden",
    errorText: "Verbindung oder Backend-Endpunkt prüfen und Seite aktualisieren.",
    loadingTitle: "Partnerdaten werden geladen",
    loadingText: "Empfehlungen, Vertragsdaten und Provisionen werden geladen.",
    rows: "Datensätze",
    quantity: "Anzahl",
    sales: "Verkäufe",
    salesListed: "Datensätze angezeigt",
    customerMissing: "Kein Kunde",
    productMissing: "Kein Produkt",
  },
};

/* =========================================================
   BAYİ EĞİTİMİ, PROJELER VE 2026 KAMPANYALI FİYATLAR
   Bu alan yalnızca frontend sunumudur; mevcut API akışına dokunmaz.
========================================================= */
const ACADEMY_TEXT = {
  tr: {
    kicker: "BÖLÜM 01 · MESYAT BAYİ AKADEMİSİ",
    title: "Bayilik Eğitim Merkezi",
    subtitle:
      "MesYat projelerini doğru kuruma doğru paketle sunun; satış görüşmesini, yıllık paketleri ve takip sürecini tek merkezden öğrenin.",
    academyTitle: "Bayilik Sistemi Nasıl Kullanılır?",
    academySubtitle:
      "Davet bağlantısından satış kaydına kadar uygulanacak temel çalışma düzeni.",
    projectsTitle: "Projelerimiz ve 2026 Kampanyalı Fiyatlar",
    projectsSubtitle:
      "Kurumun faaliyet alanına uygun projeyi seçin ve paket kapsamını açık biçimde anlatın.",
    salesTitle: "İşimi Nasıl Büyütebilirim?",
    salesSubtitle:
      "Düzenli müşteri kazanmak, doğru sektöre ulaşmak ve satış sonrası güven oluşturmak için uygulanabilir büyüme planı.",
    faqTitle: "Sıkça Sorulan Sorular",
    faqSubtitle:
      "Paket, demo, fiyat ve özel geliştirme konularında müşteriye verilecek net cevaplar.",
    packageCount: "7 profesyonel proje",
    sectionLabel: "BÖLÜM",
    annualCampaign: "2026 yıllık kampanya",
    includedFeatures: "Pakete Dahil Özellikler",
    projectColumn: "Proje / Çözüm",
    priceColumn: "Yıllık Fiyat",
    scopeColumn: "Kapsam ve Özellikler",
    perYear: "/ yıl",
    startingFrom: "başlayan fiyat",
    mostPreferred: "En Çok Tercih Edilen",
    priceNote:
      "Fiyatlar yıllık kampanyalı paket bedelleridir. Ek modül, özel entegrasyon ve kuruma özel geliştirmeler ayrıca projelendirilir; nihai kapsam satış görüşmesinde netleştirilir.",
    trainingSteps: [
      {
        title: "1. Davet bağlantınızı paylaşın",
        text: "Bayilik ekranındaki kişisel davet bağlantınızı WhatsApp, e-posta veya yüz yüze görüşme sonrasında ilgili kurumla paylaşın.",
      },
      {
        title: "2. Kurumun ihtiyacını analiz edin",
        text: "Kurum türünü, çalışma şeklini, kullanıcı sayısını ve ihtiyaç duyduğu modülleri öğrenmeden paket önermeyin.",
      },
      {
        title: "3. Doğru proje ve paketi eşleştirin",
        text: "Restoran için QR Menü, salon için Kuaför & Güzellik, dernek için Dernek & Vakıf gibi doğrudan ihtiyaca uygun çözümü sunun.",
      },
      {
        title: "4. Satış ve sözleşme kaydını takip edin",
        text: "Admin tarafından eklenen satış, sözleşme tarihi, yenileme süresi ve hak ediş bilgilerini bu sayfadaki raporlardan izleyin.",
      },
    ],
    salesSteps: [
      "Tek bir hedef sektör seçin; o sektör için örnek proje, demo ve kısa sunum hazırlayın.",
      "Her hafta ulaşılacak kurum, yapılacak demo ve gönderilecek teklif için ölçülebilir hedef koyun.",
      "Referans kodunuzu görüşme, WhatsApp ve sosyal medya paylaşımlarında düzenli kullanın.",
      "Müşteriye yalnızca yazılımı değil; zaman tasarrufu, düzen, görünürlük ve satış artışı faydasını anlatın.",
      "Mevcut müşterilerle iletişimi sürdürün; yenileme, ek modül ve farklı proje ihtiyaçlarını takip edin.",
      "Memnun müşterilerden referans isteyin ve başarılı projeleri yeni görüşmelerde güven örneği olarak kullanın.",
    ],
    faqs: [
      {
        question: "Hangi kuruma hangi proje önerilmeli?",
        answer:
          "Kurumun ana faaliyetine göre seçim yapılmalıdır. Restoran ve oteller için QR Menü; kuaför ve salonlar için Kuaför & Güzellik; dernek ve vakıflar için üye/duyuru sistemi; muhtarlıklar için vatandaş iletişim sistemi uygundur.",
      },
      {
        question: "Fiyata hangi hizmetler dahildir?",
        answer:
          "Her kartta listelenen standart modüller yıllık pakete dahildir. Kuruma özel yeni modül, dış servis entegrasyonu veya özel tasarım talepleri ayrıca değerlendirilir.",
      },
      {
        question: "Deneme süresi var mı?",
        answer:
          "Paket kartında 15 gün deneme süresi belirtilen projelerde deneme imkânı sunulur. Deneme kapsamı ve başlangıç tarihi satış görüşmesinde planlanır.",
      },
      {
        question: "Müşteri daha büyük bir paket isterse ne yapılır?",
        answer:
          "İhtiyaçlar yazılı olarak toplanır, mevcut paketle karşılaştırılır ve ek modüller ayrı kalemler hâlinde kurumsal teklife eklenir.",
      },
    ],
  },
  en: {
    kicker: "SECTION 01 · MESYAT PARTNER ACADEMY",
    title: "Partner Training Centre",
    subtitle:
      "Present the right MesYat project to the right institution and learn the sales, annual package and follow-up process in one place.",
    academyTitle: "How to Use the Partner System",
    academySubtitle: "The core workflow from invitation link to sales tracking.",
    projectsTitle: "Our Projects and 2026 Campaign Prices",
    projectsSubtitle: "Select the right project and explain its scope clearly.",
    salesTitle: "How Can I Grow My Business?",
    salesSubtitle: "A practical growth plan for consistent acquisition, focused outreach and customer trust.",
    faqTitle: "Frequently Asked Questions",
    faqSubtitle: "Clear answers about packages, trials, prices and custom work.",
    packageCount: "7 professional projects",
    sectionLabel: "SECTION",
    annualCampaign: "2026 annual campaign",
    includedFeatures: "Included Features",
    projectColumn: "Project / Solution",
    priceColumn: "Annual Price",
    scopeColumn: "Scope and Features",
    perYear: "/ year",
    startingFrom: "starting price",
    mostPreferred: "Most Preferred",
    priceNote:
      "Prices are annual campaign package prices. Extra modules, integrations and institution-specific work are scoped separately; final scope is confirmed during the sales meeting.",
    trainingSteps: [
      { title: "1. Share your invitation link", text: "Share your personal partner invitation link through WhatsApp, email or after a meeting." },
      { title: "2. Analyse the institution", text: "Learn the institution type, workflow, users and required modules before recommending a package." },
      { title: "3. Match the right project", text: "Offer QR Menu to hospitality, Beauty to salons and Association solutions to member organisations." },
      { title: "4. Track the sales record", text: "Follow admin-entered sales, contract dates, renewals and commission information from this page." },
    ],
    salesSteps: [
      "Choose one target sector and prepare a tailored project, demo and short presentation.",
      "Set measurable weekly targets for outreach, demos and proposals.",
      "Use your referral code consistently in meetings, WhatsApp and social media.",
      "Sell business value: time savings, organisation, visibility and revenue growth.",
      "Stay in contact with customers and track renewals, add-ons and new project needs.",
      "Ask satisfied customers for referrals and use successful projects as proof of trust.",
    ],
    faqs: [
      { question: "Which project fits which institution?", answer: "Choose by core activity: QR Menu for restaurants and hotels, Beauty for salons, Association for NGOs and Digital Municipality for local administrations." },
      { question: "What is included in the price?", answer: "The standard modules shown on each package card are included. Custom modules, integrations and bespoke designs are evaluated separately." },
      { question: "Is there a trial period?", answer: "Projects marked with a 15-day trial can be tested. The scope and start date are planned during the sales meeting." },
      { question: "What if the customer needs a larger package?", answer: "Collect requirements in writing, compare them with the standard package and add extra modules as separate proposal items." },
    ],
  },
  de: {
    kicker: "ABSCHNITT 01 · MESYAT PARTNER-AKADEMIE",
    title: "Partner-Schulungszentrum",
    subtitle:
      "Präsentieren Sie jeder Institution das passende MesYat-Projekt und lernen Sie Verkauf, Jahrespakete und Nachverfolgung an einem Ort.",
    academyTitle: "So verwenden Sie das Partnersystem",
    academySubtitle: "Der Ablauf vom Einladungslink bis zur Verkaufsverfolgung.",
    projectsTitle: "Unsere Projekte und Aktionspreise 2026",
    projectsSubtitle: "Passendes Projekt auswählen und den Umfang klar erklären.",
    salesTitle: "Wie kann ich mein Geschäft ausbauen?",
    salesSubtitle: "Ein praktischer Wachstumsplan für Kundengewinnung, fokussierte Ansprache und Vertrauen.",
    faqTitle: "Häufig gestellte Fragen",
    faqSubtitle: "Klare Antworten zu Paketen, Testphase, Preisen und Sonderentwicklungen.",
    packageCount: "7 professionelle Projekte",
    sectionLabel: "ABSCHNITT",
    annualCampaign: "Jahresaktion 2026",
    includedFeatures: "Enthaltene Funktionen",
    projectColumn: "Projekt / Lösung",
    priceColumn: "Jahrespreis",
    scopeColumn: "Umfang und Funktionen",
    perYear: "/ Jahr",
    startingFrom: "Startpreis",
    mostPreferred: "Am häufigsten gewählt",
    priceNote:
      "Die Preise sind jährliche Aktionspreise. Zusatzmodule, Integrationen und individuelle Entwicklungen werden separat geplant; der endgültige Umfang wird im Verkaufsgespräch festgelegt.",
    trainingSteps: [
      { title: "1. Einladungslink teilen", text: "Teilen Sie Ihren persönlichen Partnerlink per WhatsApp, E-Mail oder nach einem Gespräch." },
      { title: "2. Bedarf analysieren", text: "Ermitteln Sie Institutionstyp, Arbeitsablauf, Benutzer und benötigte Module vor der Empfehlung." },
      { title: "3. Passendes Projekt wählen", text: "QR-Menü für Gastronomie, Beauty für Salons und Vereinslösungen für Mitgliederorganisationen anbieten." },
      { title: "4. Verkauf verfolgen", text: "Verkäufe, Vertragsdaten, Verlängerungen und Provisionen auf dieser Seite verfolgen." },
    ],
    salesSteps: [
      "Eine Zielbranche wählen und dafür Projekt, Demo und Kurzpräsentation vorbereiten.",
      "Messbare Wochenziele für Kontakte, Demos und Angebote setzen.",
      "Den Empfehlungscode konsequent in Gesprächen, WhatsApp und sozialen Medien verwenden.",
      "Geschäftsnutzen verkaufen: Zeitersparnis, Ordnung, Sichtbarkeit und mehr Umsatz.",
      "Kundenkontakt pflegen und Verlängerungen, Zusatzmodule sowie neue Bedürfnisse verfolgen.",
      "Zufriedene Kunden um Empfehlungen bitten und erfolgreiche Projekte als Vertrauensbeleg nutzen.",
    ],
    faqs: [
      { question: "Welches Projekt passt zu welcher Institution?", answer: "Nach Kerntätigkeit wählen: QR-Menü für Gastronomie und Hotels, Beauty für Salons, Vereinslösung für Organisationen und digitale Verwaltung für Gemeinden." },
      { question: "Was ist im Preis enthalten?", answer: "Die auf jeder Paketkarte aufgeführten Standardmodule sind enthalten. Sondermodule, Integrationen und individuelles Design werden separat bewertet." },
      { question: "Gibt es eine Testphase?", answer: "Projekte mit ausgewiesener 15-Tage-Testphase können getestet werden. Umfang und Starttermin werden im Gespräch geplant." },
      { question: "Was tun bei größerem Paketbedarf?", answer: "Anforderungen schriftlich erfassen, mit dem Standardpaket vergleichen und Zusatzmodule als eigene Angebotspositionen aufnehmen." },
    ],
  },
};

const SECTION_TEXT = {
  tr: {
    overview: "Genel Bakış",
    referrals: "Referanslar ve Satışlarımız",
    pricing: "Proje ve Fiyatlar",
    academy: "Eğitim ve Gelişim",
    growth: "İşimi Büyüt",
    faq: "Sıkça Sorulanlar",
    reports: "Raporlar",
    records: "Satış Kayıtları",
    overviewTitle: "Bayilik Yönetim Merkezi",
    overviewText:
      "Referanslarınızı, satış performansınızı, projeleri ve eğitim içeriklerini üst menüden tek tıkla yönetin.",
    referenceCode: "Referans Kodu",
    incomingReferral: "Kayıt Yapan",
    salesRecord: "Satış Kaydı",
    salesTitle: "Satışlarımız",
    salesSubtitle:
      "Referanslarınızdan oluşan satışları müşteri, proje, tutar ve hak ediş bilgileriyle inceleyin.",
    educationTitle: "Eğitim, İş Geliştirme ve Destek Merkezi",
    educationSubtitle:
      "Bayilik eğitimlerini, işinizi büyütme adımlarını ve sıkça sorulan soruları tek panelden yönetin.",
    overviewTableTitle: "Bayilik Performans Tablosu",
    overviewTableSubtitle:
      "Toplam satış, sözleşme, yenileme ve dönemsel satış göstergeleriniz.",
  },
  en: {
    overview: "Overview",
    referrals: "Referrals & Sales",
    pricing: "Projects & Prices",
    academy: "Training & Growth",
    growth: "Grow My Business",
    faq: "FAQ",
    reports: "Reports",
    records: "Sales Records",
    overviewTitle: "Partner Management Centre",
    overviewText:
      "Manage referrals, sales performance, projects and training content from the section menu.",
    referenceCode: "Referral Code",
    incomingReferral: "Registered Referral",
    salesRecord: "Sales Record",
    salesTitle: "Our Sales",
    salesSubtitle:
      "Review referral-driven sales by customer, project, amount and commission.",
    educationTitle: "Training, Business Growth and Support Centre",
    educationSubtitle:
      "Manage partner training, growth guidance and frequently asked questions in one panel.",
    overviewTableTitle: "Partner Performance Table",
    overviewTableSubtitle:
      "Your total sales, contracts, renewals and period sales indicators.",
  },
  de: {
    overview: "Übersicht",
    referrals: "Empfehlungen & Verkäufe",
    pricing: "Projekte & Preise",
    academy: "Schulung & Wachstum",
    growth: "Geschäft ausbauen",
    faq: "Häufige Fragen",
    reports: "Berichte",
    records: "Verkaufsdaten",
    overviewTitle: "Partner-Verwaltungszentrum",
    overviewText:
      "Empfehlungen, Verkaufsleistung, Projekte und Schulungen über das Bereichsmenü verwalten.",
    referenceCode: "Empfehlungscode",
    incomingReferral: "Registrierte Empfehlung",
    salesRecord: "Verkaufsdatensatz",
    salesTitle: "Unsere Verkäufe",
    salesSubtitle:
      "Empfehlungsbasierte Verkäufe nach Kunde, Projekt, Betrag und Provision prüfen.",
    educationTitle: "Schulung, Geschäftswachstum und Support",
    educationSubtitle:
      "Partnerschulungen, Wachstumshilfen und häufige Fragen in einem Bereich verwalten.",
    overviewTableTitle: "Partner-Leistungstabelle",
    overviewTableSubtitle:
      "Gesamtumsatz, Verträge, Verlängerungen und periodische Verkaufskennzahlen.",
  },
};

const FEATURE_TEXT = {
  managementPanel: { tr: "Yönetim paneli", en: "Management panel", de: "Verwaltungspanel" },
  customApp: { tr: "Kuruma özel aplikasyon", en: "Institution-specific application", de: "Institutionsspezifische App" },
  instantPrice: { tr: "Anında fiyat güncelleme", en: "Instant price updates", de: "Sofortige Preisaktualisierung" },
  productCategory: { tr: "Ürün ve kategori yönetimi", en: "Product and category management", de: "Produkt- und Kategorieverwaltung" },
  aiCalories: { tr: "Yapay zekâ destekli kalori hesaplama", en: "AI-assisted calorie calculation", de: "KI-gestützte Kalorienberechnung" },
  nineLanguages: { tr: "9 dil desteği", en: "9-language support", de: "Unterstützung für 9 Sprachen" },
  waiterCall: { tr: "Garson çağırma sistemi", en: "Waiter call system", de: "Kellnerruf-System" },
  tableOrder: { tr: "Masadan sipariş sistemi", en: "Table ordering system", de: "Bestellung vom Tisch" },
  themes20: { tr: "20 tema seçeneği", en: "20 theme options", de: "20 Designvorlagen" },
  themes40: { tr: "40 tema seçeneği", en: "40 theme options", de: "40 Designvorlagen" },
  mobileQr: { tr: "Mobil uyumlu QR menü", en: "Mobile-friendly QR menu", de: "Mobiloptimiertes QR-Menü" },
  mobile: { tr: "Mobil uyumlu yapı", en: "Mobile-friendly structure", de: "Mobiloptimierte Struktur" },
  trial15: { tr: "15 gün deneme süresi", en: "15-day trial", de: "15 Tage Testphase" },
  pos: { tr: "Adisyon sistemi", en: "POS / check system", de: "Kassenbon-System" },
  qrMenu: { tr: "QR menü sistemi", en: "QR menu system", de: "QR-Menü-System" },
  stock: { tr: "Stok takibi", en: "Stock tracking", de: "Bestandsverfolgung" },
  cash: { tr: "Kasa takibi", en: "Cash tracking", de: "Kassenverfolgung" },
  currentAccount: { tr: "Cari takibi", en: "Account tracking", de: "Kontenverfolgung" },
  serviceCatalog: { tr: "QR dijital hizmet kataloğu", en: "QR digital service catalogue", de: "Digitaler QR-Leistungskatalog" },
  onlineAppointment: { tr: "Online randevu sistemi", en: "Online appointment system", de: "Online-Terminsystem" },
  servicePrice: { tr: "Hizmet ve fiyat yönetimi", en: "Service and price management", de: "Leistungs- und Preisverwaltung" },
  customerTracking: { tr: "Müşteri takibi", en: "Customer tracking", de: "Kundenverwaltung" },
  multilingual: { tr: "Çoklu dil desteği", en: "Multilingual support", de: "Mehrsprachige Unterstützung" },
  announcements: { tr: "Duyuru sistemi", en: "Announcement system", de: "Mitteilungssystem" },
  events: { tr: "Etkinlik yönetimi", en: "Event management", de: "Veranstaltungsverwaltung" },
  donation: { tr: "Bağış ve bilgilendirme alanı", en: "Donation and information area", de: "Spenden- und Informationsbereich" },
  memberInfo: { tr: "Üye bilgilendirme", en: "Member information", de: "Mitgliederinformation" },
  neighborhood: { tr: "Mahalle bilgilendirme alanı", en: "Neighbourhood information area", de: "Nachbarschaftsinformation" },
  citizenRequest: { tr: "Vatandaş talep sistemi", en: "Citizen request system", de: "Bürgeranfragesystem" },
  activityNotice: { tr: "Etkinlik ve faaliyet duyuruları", en: "Event and activity announcements", de: "Veranstaltungs- und Aktivitätsmeldungen" },
  qrInfo: { tr: "QR ile bilgi sayfası", en: "QR information page", de: "QR-Informationsseite" },
  photoText: { tr: "Fotoğraf ve metin alanı", en: "Photo and text area", de: "Foto- und Textbereich" },
  lifeStory: { tr: "Hayat hikâyesi alanı", en: "Life story area", de: "Lebensgeschichte" },
  multilingualContent: { tr: "Çok dilli içerik desteği", en: "Multilingual content", de: "Mehrsprachige Inhalte" },
  matchNotice: { tr: "Maç duyuruları", en: "Match announcements", de: "Spielankündigungen" },
  trainingInfo: { tr: "Antrenman bilgilendirme", en: "Training information", de: "Trainingsinformationen" },
  clubNews: { tr: "Kulüp haberleri", en: "Club news", de: "Vereinsnachrichten" },
  athleteTeam: { tr: "Sporcu ve takım tanıtımı", en: "Athlete and team profiles", de: "Sportler- und Teamprofile" },
  sponsor: { tr: "Sponsor alanları", en: "Sponsor areas", de: "Sponsorenbereiche" },
};

const PROJECT_CATALOG = [
  {
    key: "qr-menu-start",
    icon: "▦",
    category: { tr: "QR Menü Sistemleri", en: "QR Menu Systems", de: "QR-Menü-Systeme" },
    title: { tr: "QR Menü Başlangıç Paketi", en: "QR Menu Starter Package", de: "QR-Menü Starterpaket" },
    description: {
      tr: "Restoran, kafe ve işletmeler için temel QR menü yönetimi; menü, fiyat ve içeriklerin kolayca güncellendiği başlangıç çözümü.",
      en: "A starter QR menu solution for restaurants and cafés with easy menu, price and content updates.",
      de: "QR-Menü-Einstiegslösung für Restaurants und Cafés mit einfacher Menü- und Preisverwaltung.",
    },
    price: "10.000 TL",
    features: ["managementPanel", "customApp", "instantPrice", "productCategory", "aiCalories", "nineLanguages", "waiterCall", "tableOrder", "themes20", "mobileQr", "trial15"],
  },
  {
    key: "qr-menu-full",
    icon: "◆",
    popular: true,
    category: { tr: "QR Menü Sistemleri", en: "QR Menu Systems", de: "QR-Menü-Systeme" },
    title: { tr: "QR Menü Full Paket", en: "QR Menu Full Package", de: "QR-Menü Komplettpaket" },
    description: {
      tr: "QR menü, sipariş, adisyon, kasa, stok ve cari süreçlerini tek yapıda birleştiren kapsamlı dijital işletme paketi.",
      en: "A complete digital business package combining menu, orders, POS, cash, stock and accounts.",
      de: "Komplettpaket für QR-Menü, Bestellungen, Kasse, Lager und Konten in einem System.",
    },
    price: "20.000 TL",
    features: ["managementPanel", "pos", "customApp", "qrMenu", "instantPrice", "tableOrder", "waiterCall", "stock", "cash", "currentAccount", "aiCalories", "nineLanguages", "themes40", "mobile", "trial15"],
  },
  {
    key: "beauty",
    icon: "✦",
    starting: true,
    category: { tr: "Kuaför & Güzellik", en: "Hair & Beauty", de: "Friseur & Beauty" },
    title: { tr: "QR Kuaför & Güzellik Sistemi", en: "QR Hair & Beauty System", de: "QR Friseur- & Beauty-System" },
    description: {
      tr: "Kuaför ve güzellik merkezleri için QR destekli hizmet kataloğu, randevu ve müşteri yönetimi sistemi.",
      en: "QR service catalogue, appointment and customer management for salons and beauty centres.",
      de: "QR-Leistungskatalog, Termin- und Kundenverwaltung für Salons und Beauty-Center.",
    },
    price: "9.500 TL",
    features: ["serviceCatalog", "onlineAppointment", "servicePrice", "customerTracking", "multilingual", "mobile", "trial15"],
  },
  {
    key: "association",
    icon: "♟",
    category: { tr: "Dernek & Vakıf", en: "Association & Foundation", de: "Verein & Stiftung" },
    title: { tr: "Dernek & Vakıf Uygulaması", en: "Association & Foundation App", de: "Vereins- & Stiftungs-App" },
    description: {
      tr: "Dernek, vakıf ve STK’lar için duyuru, etkinlik, bağış bilgilendirme ve üye iletişim platformu.",
      en: "Announcement, event, donation information and member communication for NGOs.",
      de: "Mitteilungen, Veranstaltungen, Spendeninformation und Mitgliederkommunikation für Organisationen.",
    },
    price: "9.500 TL",
    features: ["announcements", "events", "donation", "memberInfo", "managementPanel", "mobile", "trial15"],
  },
  {
    key: "municipality",
    icon: "⌂",
    category: { tr: "Muhtarlık Sistemi", en: "Local Administration", de: "Lokale Verwaltung" },
    title: { tr: "Muhtarlık Dijital Yönetim Sistemi", en: "Digital Local Administration System", de: "Digitales Verwaltungssystem" },
    description: {
      tr: "Muhtarlıklar için duyuru, mahalle bilgilendirme, talep toplama ve dijital vatandaş iletişim sistemi.",
      en: "Announcements, neighbourhood information, requests and citizen communication for local administrations.",
      de: "Mitteilungen, Nachbarschaftsinformation, Anfragen und Bürgerkommunikation für lokale Verwaltungen.",
    },
    price: "5.000 TL",
    features: ["announcements", "neighborhood", "citizenRequest", "activityNotice", "managementPanel", "mobile", "trial15"],
  },
  {
    key: "memorial",
    icon: "◇",
    category: { tr: "QR Bilgi Sistemi", en: "QR Information System", de: "QR-Informationssystem" },
    title: { tr: "Mezar & Anıt QR Sistemi", en: "Grave & Memorial QR System", de: "Grab- & Gedenk-QR-System" },
    description: {
      tr: "Mezar, anıt, aile ve tarihî alanlar için QR kod destekli dijital hayat ve bilgilendirme sistemi.",
      en: "QR-based digital story and information pages for graves, memorials, families and historic places.",
      de: "QR-gestützte digitale Lebens- und Informationsseiten für Gräber und Gedenkstätten.",
    },
    price: "5.000 TL",
    features: ["qrInfo", "photoText", "lifeStory", "multilingualContent", "mobile"],
  },
  {
    key: "sport",
    icon: "●",
    category: { tr: "Spor Kulüpleri", en: "Sports Clubs", de: "Sportvereine" },
    title: { tr: "Spor & Futbol Sistemi", en: "Sports & Football System", de: "Sport- & Fußball-System" },
    description: {
      tr: "Spor kulüpleri ve futbol takımları için maç, antrenman, haber, oyuncu, takım ve sponsor yönetim sistemi.",
      en: "Match, training, news, player, team and sponsor management for sports clubs.",
      de: "Spiel-, Training-, Nachrichten-, Spieler-, Team- und Sponsorenverwaltung für Sportvereine.",
    },
    price: "10.000 TL",
    features: ["matchNotice", "trainingInfo", "clubNews", "athleteTeam", "sponsor", "managementPanel"],
  },
];

function getPanelLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();
  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function getLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("user_token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("admin_token") ||
    ""
  );
}

function safeText(value, fallback = "-") {
  const v = String(value || "").trim();
  return v || fallback;
}

function safeLower(value) {
  return String(value || "").toLocaleLowerCase("tr-TR");
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function normalizeCurrency(value) {
  const raw = String(value || "TRY").toUpperCase();
  if (raw === "TL") return "TRY";
  return raw;
}

function moneyText(value, currency = "TRY", lang = "tr") {
  const amount = parseNumber(value);

  const formatted = amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${formatted} ${normalizeCurrency(currency)}`;
}

function moneyTextShort(value, currency = "TRY", lang = "tr") {
  const amount = parseNumber(value);

  const formatted = amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${formatted} ${normalizeCurrency(currency)}`;
}

function percentText(value, lang = "tr") {
  const amount = parseNumber(value);

  return `${amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function dateText(value, lang = "tr") {
  if (!value) return "-";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";

    return new Intl.DateTimeFormat(getLocale(lang), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return "-";
  }
}

function dateTimeText(value, lang = "tr") {
  if (!value) return "-";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";

    return new Intl.DateTimeFormat(getLocale(lang), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "-";
  }
}

function getMonthSalesLabel(lang = "tr") {
  const now = new Date();

  const monthName = new Intl.DateTimeFormat(getLocale(lang), {
    month: "long",
  }).format(now);

  const cleanMonth =
    lang === "tr"
      ? monthName.charAt(0).toLocaleUpperCase("tr-TR") + monthName.slice(1)
      : monthName.charAt(0).toUpperCase() + monthName.slice(1);

  if (lang === "en") return `${cleanMonth} Sales`;
  if (lang === "de") return `${cleanMonth} Verkäufe`;

  return `${cleanMonth} Satış`;
}

function isCurrentMonthSale(row = {}) {
  const rawDate =
    row.anlasma_tarihi ||
    row.created_at ||
    row.sozlesme_baslangic ||
    row.raw?.anlasma_tarihi ||
    row.raw?.created_at;

  if (!rawDate) return false;

  const d = new Date(rawDate);
  const now = new Date();

  if (Number.isNaN(d.getTime())) return false;

  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function getDaysLeft(value) {

  if (!value) return null;

  const end = new Date(value);
  const now = new Date();

  if (Number.isNaN(end.getTime())) return null;

  end.setHours(23, 59, 59, 999);
  now.setHours(0, 0, 0, 0);

  const diff = end.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getProcessTypeText(value, TT) {
  const raw = String(value || "satis").toLowerCase();

  if (raw === "demo") return TT.demo;
  if (raw === "yenileme") return TT.renewal;
  if (raw === "kurulum") return TT.setup;

  return TT.sale;
}

function calculateCommission(amount, rate) {
  const sale = parseNumber(amount);
  const cleanRate = parseNumber(rate);
  return Math.round(((sale * cleanRate) / 100) * 100) / 100;
}

function getCommissionAmount(row) {
  const stored = parseNumber(row.hakedis_tutari);
  if (stored > 0) return stored;
  return calculateCommission(row.satis_tutari, row.hakedis_orani);
}

function getBuyerName(row = {}, TT = TEXT.tr) {
  return (
    row.musteri_adi ||
    row.musteri_name ||
    row.firma_adi ||
    row.company_name ||
    row.kurum_adi ||
    TT.customerMissing
  );
}

function getProductName(row = {}, TT = TEXT.tr) {
  return row.satilan_urun || row.package_name || row.paket || TT.productMissing;
}

function getRemainingChip(value, TT) {
  const days = getDaysLeft(value);

  if (days === null) {
    return {
      label: TT.dateMissing,
      bg: "#F1F5F9",
      color: "#475569",
    };
  }

  if (days < 0) {
    return {
      label: `${Math.abs(days)} ${TT.dayPassed}`,
      bg: "#FEF2F2",
      color: "#991B1B",
    };
  }

  if (days <= 15) {
    return {
      label: `${days} ${TT.dayLeft}`,
      bg: "#FEF2F2",
      color: "#991B1B",
    };
  }

  if (days <= 45) {
    return {
      label: `${days} ${TT.dayLeft}`,
      bg: "#FFF7ED",
      color: "#9A3412",
    };
  }

  return {
    label: `${days} ${TT.dayLeft}`,
    bg: "#ECFDF5",
    color: "#047857",
  };
}

function normalizeRecord(row = {}) {
  const saleAmount =
    row.satis_tutari ??
    row.sale_amount ??
    row.sales_amount ??
    row.total_amount ??
    row.amount ??
    0;

  const rate =
    row.hakedis_orani ??
    row.commission_rate ??
    row.hak_edis_orani ??
    row.rate ??
    0;

  const commission =
    row.hakedis_tutari ??
    row.commission_amount ??
    row.hak_edis ??
    row.hakedis ??
    calculateCommission(saleAmount, rate);

  return {
    id:
      row.id ??
      row.sale_id ??
      row.contract_id ??
      `${row.kurum_id || row.company_id || "record"}-${
        row.created_at || Date.now()
      }`,

    kurum_id: row.kurum_id ?? row.company_id ?? row.business_id ?? null,
    kurum_old_id: row.kurum_old_id ?? row.old_id ?? null,

    referans: row.referans ?? row.partner ?? row.seller ?? "",
    firma_adi:
      row.firma_adi ??
      row.company_name ??
      row.kurum_adi ??
      row.kurum_adı ??
      row.business_name ??
      row.ad ??
      "",

    satilan_urun:
      row.satilan_urun ??
      row.package_name ??
      row.paket ??
      row.plan_name ??
      row.product_name ??
      "",

    islem_tipi: row.islem_tipi ?? row.process_type ?? "satis",

    satis_tutari: parseNumber(saleAmount),
    currency: row.currency ?? row.para_birimi ?? "TRY",

    hakedis_kademe: row.hakedis_kademe ?? row.commission_step ?? "1",
    hakedis_1_oran: parseNumber(row.hakedis_1_oran ?? 0),
    hakedis_2_oran: parseNumber(row.hakedis_2_oran ?? 0),
    hakedis_3_oran: parseNumber(row.hakedis_3_oran ?? 0),
    hakedis_orani: parseNumber(rate),
    hakedis_tutari: parseNumber(commission),

    musteri_kurum_id: row.musteri_kurum_id ?? row.customer_company_id ?? null,
    musteri_old_id: row.musteri_old_id ?? row.customer_old_id ?? null,
    musteri_adi: row.musteri_adi ?? row.customer_name ?? "",
    musteri_email: row.musteri_email ?? row.customer_email ?? "",
    musteri_code: row.musteri_code ?? row.customer_code ?? "",
    musteri_short_code: row.musteri_short_code ?? row.customer_short_code ?? "",

    anlasma_tarihi:
      row.anlasma_tarihi ??
      row.sale_date ??
      row.satis_tarihi ??
      row.created_at ??
      "",

    sozlesme_baslangic:
      row.sozlesme_baslangic ??
      row.contract_start ??
      row.baslangic_tarihi ??
      row.start_date ??
      "",

    sozlesme_bitis:
      row.sozlesme_bitis ??
      row.contract_end ??
      row.bitis_tarihi ??
      row.end_date ??
      "",

    anlasma_notu:
      row.anlasma_notu ??
      row.note ??
      row.not ??
      row.description ??
      row.aciklama ??
      row.açıklama ??
      "",

    created_at: row.created_at ?? row.createdAt ?? "",
    raw: row,
  };
}

function normalizeDashboard(json = {}) {
  const data = json.data || json;

  const recordsRaw =
    data.islemler ||
    json.islemler ||
    data.records ||
    data.sales ||
    json.sales ||
    data.satislar ||
    [];

  const partnerRaw = data.partner || data.user || data.profile || {};

  const records = Array.isArray(recordsRaw)
    ? recordsRaw.map(normalizeRecord)
    : [];

  const summaryRaw = data.summary || json.summary || {};

  const davetLinkiRaw = data.davet_linki || json.davet_linki || {};
  const davetlerRaw = data.davetler || json.davetler || [];

  return {
    partner: {
      id: partnerRaw.id ?? partnerRaw.user_id ?? null,
      name:
        partnerRaw.name ??
        partnerRaw.full_name ??
        partnerRaw.ad_soyad ??
        partnerRaw.kurum_adi ??
        partnerRaw.email ??
        "",
      email: partnerRaw.email ?? partnerRaw.mail ?? "",
      code:
        partnerRaw.code ??
        partnerRaw.panel_code ??
        partnerRaw.kurum_code ??
        partnerRaw.short_code ??
        davetLinkiRaw.ref_code ??
        "",
    },
    davetLinki: {
      ref_code:
        davetLinkiRaw.ref_code ??
        partnerRaw.code ??
        partnerRaw.short_code ??
        partnerRaw.kurum_code ??
        "",
      link: davetLinkiRaw.link || "",
      whatsapp_link: davetLinkiRaw.whatsapp_link || "",
    },
    davetler: Array.isArray(davetlerRaw) ? davetlerRaw : [],
    records,
    summary: {
      toplam_islem:
        summaryRaw.toplam_islem ?? summaryRaw.total_records ?? records.length,
      aktif_sozlesme:
        summaryRaw.aktif_sozlesme ?? summaryRaw.active_contracts ?? 0,
      yaklasan_sozlesme:
        summaryRaw.yaklasan_sozlesme ?? summaryRaw.upcoming_renewals ?? 0,
    },
  };
}

async function apiJson(path, options = {}) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      ...(options.headers || {}),
    },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || json.error || `HTTP ${res.status}`);
  }

  return json;
}

function csvValue(value) {
  const raw = String(value ?? "");
  return `"${raw.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const content = rows.map((r) => r.map(csvValue).join(";")).join("\n");

  const blob = new Blob(["\uFEFF" + content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

function buildCsvRows(records, lang, TT) {
  return [
    [
      TT.partner,
      TT.company,
      TT.customerAccount,
      TT.processType,
      TT.packageName,
      TT.saleAmount,
      TT.commissionStep,
      TT.rate,
      TT.commissionAmount,
      TT.agreementDate,
      TT.startDate,
      TT.endDate,
      TT.remainingDay,
      TT.note,
    ],
    ...records.map((row) => {
      const chip = getRemainingChip(row.sozlesme_bitis, TT);

      return [
        row.referans,
        row.firma_adi,
        [
          safeText(row.musteri_adi || row.firma_adi, ""),
          row.musteri_email,
          row.musteri_code ? `Kod: ${row.musteri_code}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
        getProcessTypeText(row.islem_tipi, TT),
        row.satilan_urun,
        moneyText(row.satis_tutari, row.currency, lang),
        `${row.hakedis_kademe || "1"}. ${TT.commissionStep}`,
        percentText(row.hakedis_orani, lang),
        moneyText(getCommissionAmount(row), row.currency, lang),
        dateText(row.anlasma_tarihi, lang),
        dateText(row.sozlesme_baslangic, lang),
        dateText(row.sozlesme_bitis, lang),
        chip.label,
        row.anlasma_notu || "",
      ];
    }),
  ];
}

function buildReportRows(records, getKey, getSubText, TT) {
  const map = new Map();

  records.forEach((row) => {
    const rawKey = safeText(getKey(row), TT.noReport);
    const key = rawKey || TT.noReport;

    if (!map.has(key)) {
      map.set(key, {
        key,
        label: key,
        subText: getSubText ? safeText(getSubText(row), "") : "",
        count: 0,
        saleAmount: 0,
        commissionAmount: 0,
      });
    }

    const current = map.get(key);
    current.count += 1;
    current.saleAmount += parseNumber(row.satis_tutari);
    current.commissionAmount += getCommissionAmount(row);

    if (!current.subText && getSubText) {
      current.subText = safeText(getSubText(row), "");
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.saleAmount !== a.saleAmount) return b.saleAmount - a.saleAmount;
    return b.count - a.count;
  });
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    bgcolor: "#fff",
    fontWeight: 750,
    minWidth: 0,
    "& fieldset": {
      borderColor: BRAND.border,
    },
    "&:hover fieldset": {
      borderColor: alpha(BRAND.blue2, 0.34),
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND.blue2,
    },
  },
};

const selectSx = {
  borderRadius: 3,
  bgcolor: "#fff",
  fontWeight: 850,
  minWidth: 0,
  "& fieldset": {
    borderColor: BRAND.border,
  },
};

export default function BayilikSistemi() {
  const cardMode = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);

  const [lang, setLang] = useState(getPanelLang);
  const [records, setRecords] = useState([]);
  const [partner, setPartner] = useState({});
  const [davetLinki, setDavetLinki] = useState({});
  const [davetler, setDavetler] = useState([]);
  const [summary, setSummary] = useState({
    toplam_islem: 0,
    aktif_sozlesme: 0,
    yaklasan_sozlesme: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("overview");

  const TT = TEXT[lang] || TEXT.tr;
  const ST = SECTION_TEXT[lang] || SECTION_TEXT.tr;

  useEffect(() => {
    localStorage.setItem("panel_dili", lang);
  }, [lang]);

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-bayilik-mobile-fix", "true");
    style.innerHTML = `
      html,
      body,
      #root {
        max-width: 100%;
      }

      .bayilik-root,
      .bayilik-root * {
        box-sizing: border-box;
      }

      .bayilik-root {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
      }

      .bayilik-root img,
      .bayilik-root svg,
      .bayilik-root canvas,
      .bayilik-root video {
        max-width: 100%;
      }

      .bayilik-root .MuiCard-root,
      .bayilik-root .MuiPaper-root,
      .bayilik-root .MuiStack-root,
      .bayilik-root .MuiGrid-root {
        min-width: 0;
        max-width: 100%;
      }

      .bayilik-root .MuiTypography-root {
        max-width: 100%;
      }

      .bayilik-root .MuiButton-root,
      .bayilik-root .MuiTab-root,
      .bayilik-root .MuiChip-root {
        min-width: 0;
      }

      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        html,
        body,
        #root {
          overflow-x: hidden;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchData = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const json = await apiJson(ENDPOINTS.dashboard);
        const normalized = normalizeDashboard(json);

                setPartner(normalized.partner);
        setDavetLinki(normalized.davetLinki || {});
        setDavetler(normalized.davetler || []);
        setRecords(normalized.records);
        setSummary(normalized.summary);
        setLastUpdated(new Date().toISOString());

      } catch (err) {
        console.error("Bayilik bilgileri alınamadı:", err);
        setError(err.message || TT.errorText);
        setRecords([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [TT.errorText]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const filteredRecords = useMemo(() => {
    const q = safeLower(search);

    if (!q) return records;

    return records.filter((row) => {
      const text = safeLower(
        [
          row.referans,
          row.firma_adi,
          row.satilan_urun,
          row.islem_tipi,
          row.anlasma_notu,
          row.musteri_adi,
          row.musteri_email,
          row.musteri_code,
          row.kurum_id,
          row.kurum_old_id,
        ].join(" ")
      );

      return text.includes(q);
    });
  }, [records, search]);

 const totals = useMemo(() => {
  return records.reduce(
    (acc, row) => {
      acc.totalSales += parseNumber(row.satis_tutari);
      acc.totalCommission += getCommissionAmount(row);

      if (isCurrentMonthSale(row)) {
        acc.currentMonthSalesCount += 1;
      }

      const days = getDaysLeft(row.sozlesme_bitis);

      if (days !== null && days >= 0) {
        acc.activeContracts += 1;
      }

      if (days !== null && days >= 0 && days <= 45) {
        acc.upcomingRenewals += 1;
      }

      return acc;
    },
    {
      totalSales: 0,
      totalCommission: 0,
      activeContracts: 0,
      upcomingRenewals: 0,
      currentMonthSalesCount: 0,
    }
  );
}, [records]);

 const stats = {
  totalRecords: summary.toplam_islem || records.length,
  activeContracts: summary.aktif_sozlesme || totals.activeContracts,
  upcomingRenewals: summary.yaklasan_sozlesme || totals.upcomingRenewals,
  currentMonthSalesCount: totals.currentMonthSalesCount,
  currentMonthSalesLabel: getMonthSalesLabel(lang),
  totalSales: totals.totalSales,
  totalCommission: totals.totalCommission,
};

  const reportData = useMemo(() => {
    return {
      byCustomer: buildReportRows(
        filteredRecords,
        (row) => getBuyerName(row, TT) || TT.customerMissing,
        (row) =>
          [
            row.musteri_email,
            row.musteri_code ? `Kod: ${row.musteri_code}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
        TT
      ),
      byProduct: buildReportRows(
        filteredRecords,
        (row) => getProductName(row, TT) || TT.productMissing,
        (row) => getProcessTypeText(row.islem_tipi, TT),
        TT
      ),
    };
  }, [filteredRecords, TT]);

  const handleExport = () => {
    downloadCsv(
      `bayilik-bilgi-kayitlari-${new Date().toISOString().slice(0, 10)}.csv`,
      buildCsvRows(filteredRecords, lang, TT)
    );
  };

  if (loading) {
    return (
      <PageFrame>
        <LoadingView TT={TT} />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <HeroSection
        TT={TT}
        lang={lang}
        setLang={setLang}
        partner={partner}
        refreshing={refreshing}
        lastUpdated={lastUpdated}
        onRefresh={() => fetchData(true)}
        onExport={handleExport}
        cardMode={cardMode}
      />

      <SectionNavigation
        lang={lang}
        activeSection={activeSection}
        onChange={setActiveSection}
      />

      {error && <ErrorCard TT={TT} text={error} />}

      {activeSection === "overview" && (
        <StatsGrid TT={TT} lang={lang} stats={stats} />
      )}

      {activeSection === "referrals" && (
        <>
          <ReferralLinkCard davetLinki={davetLinki} davetler={davetler} />

          <ReportsAccordion
            TT={TT}
            lang={lang}
            records={filteredRecords}
            reportData={reportData}
            title={ST.salesTitle}
            subtitle={ST.salesSubtitle}
          />

          <SalesAccordion TT={TT} total={filteredRecords.length}>
            <RecordsHeader
              TT={TT}
              search={search}
              setSearch={setSearch}
              total={filteredRecords.length}
              cardMode={cardMode}
            />

            {!filteredRecords.length ? (
              <EmptyState TT={TT} />
            ) : (
              <Stack spacing={{ xs: 1.1, md: 1.6 }}>
                {filteredRecords.map((row) => (
                  <RecordCard key={row.id} row={row} TT={TT} lang={lang} />
                ))}
              </Stack>
            )}
          </SalesAccordion>
        </>
      )}

      {activeSection === "pricing" && <ProjectPricingTableSection lang={lang} />}

      {activeSection === "academy" && <EducationDevelopmentPanel lang={lang} />}
    </PageFrame>
  );
}

function PageFrame({ children }) {
  return (
    <Box
      className="bayilik-root"
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        overflowX: "hidden",
        p: {
          xs: 1,
          sm: 1.2,
          md: 0.8,
        },
        pb: {
          xs: 5,
          md: 7,
        },
        background:
          "radial-gradient(circle at top left, rgba(21,101,192,.12), transparent 32%), radial-gradient(circle at top right, rgba(245,130,32,.13), transparent 30%), linear-gradient(145deg, #F8FAFC 0%, #EEF2F7 100%)",
        color: BRAND.dark,

        "& *": {
          boxSizing: "border-box",
        },

        "& .MuiCard-root, & .MuiPaper-root": {
          maxWidth: "100%",
          minWidth: 0,
        },

        "& .MuiButton-root": {
          minWidth: 0,
        },

        "& .MuiStack-root": {
          minWidth: 0,
        },

        "& .MuiTypography-root": {
          maxWidth: "100%",
        },

        [`@media (min-width:${MOBILE_BREAKPOINT + 1}px)`]: {
          width: "calc(100vw - 230px)",
          maxWidth: "calc(100vw - 230px)",
          marginLeft: "50%",
          transform: "translateX(calc(-50vw + 115px))",
        },

        [`@media (max-width:${MOBILE_BREAKPOINT}px)`]: {
          p: 1,
          overflowX: "hidden",
          transform: "none",
          width: "100%",
          maxWidth: "100%",
          marginLeft: 0,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          mx: 0,
          overflowX: "hidden",
          animation: `${fadeUp} .35s ease both`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function LoadingView({ TT }) {
  return (
    <Box
      sx={{
        minHeight: {
          xs: "70vh",
          md: "78vh",
        },
        display: "grid",
        placeItems: "center",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: {
            xs: 3,
            md: 5,
          },
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15,23,42,.10)",
          border: `1px solid ${BRAND.border}`,
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 2,
              md: 4,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
          >
            <Avatar
              sx={{
                width: 58,
                height: 58,
                bgcolor: BRAND.softBlue,
                color: BRAND.blue,
              }}
            >
              <HandshakeIcon />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  fontSize: {
                    xs: 22,
                    md: 30,
                  },
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {TT.loadingTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  color: BRAND.muted,
                  fontWeight: 700,
                  fontSize: {
                    xs: 13,
                    md: 15,
                  },
                  lineHeight: 1.55,
                }}
              >
                {TT.loadingText}
              </Typography>
            </Box>

            <CircularProgress
              size={34}
              thickness={5}
              sx={{
                color: BRAND.orange,
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function HeroSection({
  TT,
  lang,
  setLang,
  partner,
  refreshing,
  lastUpdated,
  onRefresh,
  onExport,
  cardMode,
}) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: {
          xs: 1.2,
          md: 1.6,
        },
        borderRadius: {
          xs: 3,
          md: 4,
        },
        overflow: "hidden",
        position: "relative",
        color: "#fff",
        border: "1px solid rgba(255,255,255,.18)",
        background:
          "linear-gradient(135deg, rgba(15,23,42,.98), rgba(11,77,139,.96) 52%, rgba(245,130,32,.92))",
        boxShadow: {
          xs: "0 16px 40px rgba(15,23,42,.18)",
          md: "0 22px 54px rgba(15,23,42,.18)",
        },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.3,
          background:
            "linear-gradient(110deg, transparent 0%, rgba(255,255,255,.16) 40%, transparent 70%)",
          backgroundSize: "180% 100%",
          animation: `${shine} 4.8s linear infinite`,
        }}
      />

      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: {
            xs: 1.4,
            sm: 2,
            md: 2.4,
          },
          minWidth: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={{
            xs: 1.4,
            md: 2,
          }}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
              order: {
                xs: 2,
                md: 1,
              },
            }}
          >
            <Avatar
              sx={{
                width: {
                  xs: 58,
                  md: 66,
                },
                height: {
                  xs: 58,
                  md: 66,
                },
                bgcolor: "rgba(255,255,255,.14)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,.22)",
                boxShadow: "0 18px 40px rgba(0,0,0,.20)",
                animation: `${floatSoft} 4s ease-in-out infinite`,
                flexShrink: 0,
              }}
            >
              <HandshakeIcon
                sx={{
                  fontSize: {
                    xs: 32,
                    md: 36,
                  },
                }}
              />
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              {(partner?.code || partner?.email) && (
                <Chip
                  size="small"
                  label={partner?.code || partner?.email}
                  sx={{
                    mb: 0.8,
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFEDD5",
                    border: "1px solid rgba(255,255,255,.16)",
                    fontWeight: 900,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              )}

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-.045em",
                  lineHeight: 1.05,
                  fontSize: {
                    xs: 32,
                    sm: 36,
                    md: 42,
                  },
                  wordBreak: "break-word",
                }}
              >
                {TT.pageTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  maxWidth: 880,
                  color: "#DBEAFE",
                  fontWeight: 650,
                  fontSize: {
                    xs: 16,
                    md: 15,
                  },
                  lineHeight: {
                    xs: 1.55,
                    md: 1.45,
                  },
                  wordBreak: "break-word",
                }}
              >
                {TT.pageSubtitle}
              </Typography>
            </Box>
          </Stack>

          <Stack
            spacing={0.9}
            alignItems={{
              xs: "flex-start",
              md: "flex-end",
            }}
            sx={{
              minWidth: 0,
              width: {
                xs: "100%",
                md: "auto",
              },
              order: {
                xs: 1,
                md: 2,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.7}
              justifyContent="flex-start"
              sx={{
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={onRefresh}
                disabled={refreshing}
                startIcon={
                  refreshing ? (
                    <CircularProgress size={13} sx={{ color: "#fff" }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  minHeight: {
                    xs: 34,
                    md: 32,
                  },
                  px: 1.15,
                  borderRadius: 2.2,
                  fontSize: 12,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.14)",
                  border: "1px solid rgba(255,255,255,.14)",
                  textTransform: "none",
                  fontWeight: 900,
                  width: "auto",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.22)",
                  },
                }}
              >
                {refreshing ? TT.refreshing : TT.refresh}
              </Button>

              <Button
                onClick={onExport}
                startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: {
                    xs: 34,
                    md: 32,
                  },
                  px: 1.15,
                  borderRadius: 2.2,
                  fontSize: 12,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.14)",
                  border: "1px solid rgba(255,255,255,.14)",
                  textTransform: "none",
                  fontWeight: 900,
                  width: "auto",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.22)",
                  },
                }}
              >
                {TT.exportCsv}
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{
                xs: "flex-start",
                md: "flex-end",
              }}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <Select
                size="small"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                sx={{
                  ...selectSx,
                  minWidth: cardMode ? 106 : 118,
                  minHeight: {
                    xs: 38,
                    md: 34,
                  },
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.12)",
                  borderRadius: 2.5,
                  "& fieldset": {
                    borderColor: "rgba(255,255,255,.20)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
                startAdornment={
                  <LanguageIcon
                    sx={{
                      mr: 0.7,
                      fontSize: 18,
                      color: "#fff",
                    }}
                  />
                }
              >
                <MenuItem value="tr">TR</MenuItem>
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="de">DE</MenuItem>
              </Select>

              <Typography
                sx={{
                  color: "#DBEAFE",
                  fontWeight: 750,
                  fontSize: 11.5,
                  textAlign: "right",
                  display: {
                    xs: "none",
                    sm: "block",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 210,
                }}
              >
                {TT.lastUpdate}:{" "}
                {lastUpdated ? dateTimeText(lastUpdated, lang) : TT.never}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionNavigation({ lang, activeSection, onChange }) {
  const S = SECTION_TEXT[lang] || SECTION_TEXT.tr;
  const items = [
    { key: "overview", icon: "▦", label: S.overview },
    { key: "referrals", icon: "↗", label: S.referrals },
    { key: "pricing", icon: "₺", label: S.pricing },
    { key: "academy", icon: "✓", label: S.academy },
  ];

  return (
    <Card
      component="nav"
      aria-label={S.overviewTitle}
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        p: { xs: 0.7, md: 0.8 },
        width: "100%",
        minWidth: 0,
        border: `1px solid ${BRAND.border}`,
        borderRadius: { xs: 3, md: 3.5 },
        bgcolor: "rgba(255,255,255,.94)",
        boxShadow: "0 12px 32px rgba(15,23,42,.075)",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 0.6, md: 0.7 },
          minWidth: 0,
        }}
      >
        {items.map((item) => {
          const selected = activeSection === item.key;

          return (
            <Button
              key={item.key}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(item.key)}
              disableElevation
              sx={{
                minWidth: 0,
                minHeight: { xs: 58, md: 60 },
                px: { xs: 0.75, md: 0.9 },
                py: 0.65,
                display: "grid",
                gridTemplateColumns: "32px minmax(0, 1fr)",
                gap: 0.75,
                alignItems: "center",
                justifyContent: "stretch",
                textAlign: "left",
                textTransform: "none",
                color: selected ? "#fff" : BRAND.dark,
                bgcolor: selected ? BRAND.blue : "#fff",
                border: selected
                  ? `1px solid ${BRAND.blue}`
                  : `1px solid ${BRAND.border}`,
                borderBottom: selected
                  ? `4px solid ${BRAND.orange}`
                  : `4px solid transparent`,
                borderRadius: 2.2,
                boxShadow: selected
                  ? "0 10px 22px rgba(11,77,139,.18)"
                  : "0 4px 12px rgba(15,23,42,.035)",
                transition: "background-color .18s ease, color .18s ease, transform .18s ease",
                "&:hover": {
                  bgcolor: selected ? BRAND.blue : "#F8FAFC",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 1.7,
                  fontSize: 15,
                  fontWeight: 950,
                  color: selected ? BRAND.blue : "#526579",
                  bgcolor: selected ? "#fff" : "#EEF4FA",
                  border: selected
                    ? "1px solid rgba(255,255,255,.72)"
                    : "1px solid #D9E5F0",
                }}
              >
                {item.icon}
              </Box>

              <Typography
                component="span"
                sx={{
                  minWidth: 0,
                  color: "inherit",
                  fontSize: { xs: 11.2, md: 11.5 },
                  fontWeight: 950,
                  lineHeight: 1.18,
                  overflowWrap: "anywhere",
                }}
              >
                {item.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
    </Card>
  );
}

function OverviewSection({ lang, refCode, referralCount, totalRecords }) {
  const S = SECTION_TEXT[lang] || SECTION_TEXT.tr;
  const items = [
    { label: S.referenceCode, value: refCode || "—", color: BRAND.blue },
    { label: S.incomingReferral, value: referralCount || 0, color: "#0F766E" },
    { label: S.salesRecord, value: totalRecords || 0, color: BRAND.orange },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        borderRadius: { xs: 3, md: 4 },
        bgcolor: "#fff",
        boxShadow: "0 14px 36px rgba(15,23,42,.065)",
      }}
    >
      <Box sx={{ height: 5, bgcolor: BRAND.orange }} />
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, "&:last-child": { pb: { xs: 1.5, md: 2 } } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(260px, 1.25fr) minmax(0, 1fr)" },
            gap: { xs: 1.4, md: 2 },
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                bgcolor: BRAND.dark,
                color: "#fff",
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              M
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: BRAND.dark, fontSize: { xs: 18, md: 22 }, fontWeight: 950, lineHeight: 1.15 }}>
                {S.overviewTitle}
              </Typography>
              <Typography sx={{ mt: 0.5, color: BRAND.muted, fontSize: { xs: 12, md: 13 }, fontWeight: 650, lineHeight: 1.55 }}>
                {S.overviewText}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              gap: 0.75,
            }}
          >
            {items.map((item) => (
              <Box
                key={item.label}
                sx={{
                  minWidth: 0,
                  p: 1,
                  border: `1px solid ${BRAND.border}`,
                  borderLeft: `4px solid ${item.color}`,
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                }}
              >
                <Typography sx={{ color: BRAND.muted, fontSize: 10.5, fontWeight: 850, lineHeight: 1.2 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ mt: 0.35, color: BRAND.dark, fontSize: 16, fontWeight: 950, lineHeight: 1.15, overflowWrap: "anywhere" }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ErrorCard({ TT, text }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.4,
        borderRadius: 3,
        border: "1px solid rgba(239,68,68,.24)",
        bgcolor: BRAND.softRed,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.4,
            md: 1.7,
          },
          "&:last-child": {
            pb: {
              xs: 1.4,
              md: 1.7,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: 950,
            color: BRAND.danger,
            mb: 0.35,
          }}
        >
          {TT.errorTitle}
        </Typography>

        <Typography
          sx={{
            color: BRAND.muted,
            fontWeight: 750,
            fontSize: 13,
          }}
        >
          {text || TT.errorText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ReferralLinkCard({ davetLinki = {}, davetler = [] }) {
  const [copied, setCopied] = useState(false);

  const refCode = davetLinki.ref_code || "";
  const link =
    davetLinki.link ||
    (refCode
      ? `https://panel.haymestasarim.com/#/REF/${encodeURIComponent(refCode)}`
      : "");

  const bekleyen = davetler.filter((x) => x.durum === "kayit_bekliyor").length;
  const aday = davetler.filter((x) => x.durum === "bayi_adayi").length;
  const aktif = davetler.filter(
    (x) => x.durum === "aktif_bayi" || x.bayi_aktif === true
  ).length;
  const reddedilen = davetler.filter((x) => x.durum === "reddedildi").length;

  const getDurumText = (durum) => {
    if (durum === "kayit_bekliyor") return "Onay Bekliyor";
    if (durum === "bayi_adayi") return "Bayi Adayı";
    if (durum === "aktif_bayi") return "Aktif Bayi";
    if (durum === "reddedildi") return "Reddedildi";
    if (durum === "pasif") return "Pasif";
    return durum || "-";
  };

  const getDurumStyle = (durum) => {
    if (durum === "aktif_bayi") {
      return {
        bgcolor: BRAND.softGreen,
        color: "#047857",
        border: "1px solid rgba(4,120,87,.18)",
      };
    }

    if (durum === "bayi_adayi") {
      return {
        bgcolor: BRAND.softBlue,
        color: BRAND.blue,
        border: "1px solid rgba(21,101,192,.18)",
      };
    }

    if (durum === "reddedildi" || durum === "pasif") {
      return {
        bgcolor: BRAND.softRed,
        color: "#B91C1C",
        border: "1px solid rgba(185,28,28,.18)",
      };
    }

    return {
      bgcolor: BRAND.softYellow,
      color: "#B45309",
      border: "1px solid rgba(180,83,9,.18)",
    };
  };

  const copyLink = async () => {
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: {
          xs: 1.2,
          md: 1.5,
        },
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        boxShadow: "0 14px 36px rgba(15,23,42,.06)",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.4,
            md: 1.8,
          },
          "&:last-child": {
            pb: {
              xs: 1.4,
              md: 1.8,
            },
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1.4}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: BRAND.softOrange,
                color: "#9A3412",
                width: 50,
                height: 50,
                flexShrink: 0,
              }}
            >
              <HandshakeIcon />
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: BRAND.dark,
                  fontSize: {
                    xs: 20,
                    md: 23,
                  },
                  lineHeight: 1.1,
                }}
              >
                Referans Kodun: {refCode || "-"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: 13,
                  wordBreak: "break-all",
                }}
              >
                {link || "Davet linki henüz oluşmadı."}
              </Typography>

              <Stack direction="row" spacing={0.7} sx={{ mt: 1, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={`${davetler.length} kayıt`}
                  sx={{
                    bgcolor: BRAND.softBlue,
                    color: BRAND.blue,
                    fontWeight: 950,
                  }}
                />

                <Chip
                  size="small"
                  label={`${bekleyen} bekleyen`}
                  sx={{
                    bgcolor: BRAND.softYellow,
                    color: "#B45309",
                    fontWeight: 950,
                  }}
                />

                <Chip
                  size="small"
                  label={`${aday} aday`}
                  sx={{
                    bgcolor: "#EEF2FF",
                    color: "#3730A3",
                    fontWeight: 950,
                  }}
                />

                <Chip
                  size="small"
                  label={`${aktif} aktif`}
                  sx={{
                    bgcolor: BRAND.softGreen,
                    color: "#047857",
                    fontWeight: 950,
                  }}
                />

                <Chip
                  size="small"
                  label={`${reddedilen} reddedildi`}
                  sx={{
                    bgcolor: BRAND.softRed,
                    color: "#B91C1C",
                    fontWeight: 950,
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            sx={{
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            <Button
              onClick={copyLink}
              disabled={!link}
              sx={{
                borderRadius: 2.5,
                px: 2,
                py: 1,
                bgcolor: BRAND.blue2,
                color: "#fff",
                textTransform: "none",
                fontWeight: 950,
                "&:hover": {
                  bgcolor: BRAND.blue,
                },
                "&.Mui-disabled": {
                  bgcolor: "#CBD5E1",
                  color: "#fff",
                },
              }}
            >
              {copied ? "Kopyalandı ✅" : "Linki Kopyala"}
            </Button>

            <Button
              component="a"
              href={
                davetLinki.whatsapp_link ||
                `https://wa.me/?text=${encodeURIComponent(
                  `MesYat Dijital bayi başvuru linkim: ${link}`
                )}`
              }
              target="_blank"
              rel="noopener noreferrer"
              disabled={!link}
              sx={{
                borderRadius: 2.5,
                px: 2,
                py: 1,
                bgcolor: "#16A34A",
                color: "#fff",
                textTransform: "none",
                fontWeight: 950,
                "&:hover": {
                  bgcolor: "#15803D",
                },
                "&.Mui-disabled": {
                  bgcolor: "#CBD5E1",
                  color: "#fff",
                },
              }}
            >
              WhatsApp Paylaş
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.6 }} />

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid #D9E2EC",
            borderRadius: "14px !important",
            overflow: "hidden",
            bgcolor: "#fff",
            "&:before": { display: "none" },
            "&.Mui-expanded": { m: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<KeyboardArrowDownIcon sx={{ color: BRAND.blue }} />}
            sx={{
              minHeight: { xs: 68, md: 76 },
              px: { xs: 1.2, md: 1.5 },
              bgcolor: "#F8FAFC",
              "&.Mui-expanded": { minHeight: { xs: 68, md: 76 } },
              "& .MuiAccordionSummary-content": { my: 1, minWidth: 0 },
              "& .MuiAccordionSummary-content.Mui-expanded": { my: 1 },
              "& .MuiAccordionSummary-expandIconWrapper": {
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                borderRadius: 2.2,
                bgcolor: BRAND.softBlue,
              },
            }}
          >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
            spacing={1}
            sx={{ width: "100%", minWidth: 0, pr: 1 }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: BRAND.dark,
                  fontSize: {
                    xs: 18,
                    md: 20,
                  },
                }}
              >
                Referans Koduyla Kayıt Yapanlar
              </Typography>

              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: 13,
                  mt: 0.3,
                }}
              >
                Bu liste, senin referans kodunla başvuru yapan kişi ve firmaları gösterir.
              </Typography>
            </Box>

            <Chip
              label={`${davetler.length} toplam kayıt`}
              sx={{
                bgcolor: BRAND.softOrange,
                color: "#9A3412",
                fontWeight: 950,
              }}
            />
          </Stack>

          </AccordionSummary>

          <AccordionDetails sx={{ p: { xs: 1, md: 1.25 }, bgcolor: "#fff" }}>

          {!!davetler.length && (
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns:
                  "minmax(0, 1.2fr) minmax(0, 1.2fr) 130px 150px",
                gap: 1,
                px: 1.15,
                py: 0.85,
                mb: 0.7,
                borderRadius: 2.2,
                color: "#fff",
                bgcolor: BRAND.dark,
              }}
            >
              {[
                "KİŞİ VE İLETİŞİM",
                "FİRMA VE KURUM KODU",
                "DURUM",
                "KAYIT TARİHİ",
              ].map((label, index) => (
                <Typography
                  key={label}
                  sx={{
                    fontSize: 10,
                    fontWeight: 950,
                    letterSpacing: ".065em",
                    textAlign: index >= 2 ? "center" : "left",
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          )}

          {!davetler.length ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "#F8FAFC",
                border: `1px dashed ${BRAND.border2}`,
                color: BRAND.muted,
                fontWeight: 850,
                textAlign: "center",
              }}
            >
              Henüz bu referans kodla kayıt yapan yok.
            </Box>
          ) : (
            <Stack spacing={0.9}>
              {davetler.map((davet) => (
                <Box
                  key={davet.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(0, 1.2fr) minmax(0, 1.2fr) 130px 150px",
                    },
                    gap: {
                      xs: 0.8,
                      md: 1,
                    },
                    alignItems: "center",
                    p: {
                      xs: 1,
                      md: 1.15,
                    },
                    borderRadius: 3,
                    bgcolor: "#F8FAFC",
                    border: `1px solid ${BRAND.border}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: BRAND.dark,
                        fontWeight: 950,
                        fontSize: 15,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {davet.ad_soyad || "-"}
                    </Typography>

                    <Typography
                      sx={{
                        color: BRAND.muted,
                        fontWeight: 750,
                        fontSize: 12.5,
                        mt: 0.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {davet.email || "-"} · {davet.telefon || "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: BRAND.dark,
                        fontWeight: 900,
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {davet.firma_adi || "-"}
                    </Typography>

                    <Typography
                      sx={{
                        color: BRAND.muted,
                        fontWeight: 750,
                        fontSize: 12.5,
                        mt: 0.2,
                      }}
                    >
                      Kod: {davet.talep_code || "-"} · Short:{" "}
                      {davet.talep_short_code || "-"}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={getDurumText(davet.durum)}
                    sx={{
                      justifySelf: {
                        xs: "flex-start",
                        md: "center",
                      },
                      fontWeight: 950,
                      ...getDurumStyle(davet.durum),
                    }}
                  />

                  <Typography
                    sx={{
                      color: BRAND.muted,
                      fontWeight: 800,
                      fontSize: 12.5,
                      textAlign: {
                        xs: "left",
                        md: "right",
                      },
                    }}
                  >
                    {dateText(davet.created_at)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
}
function StatsGrid({ TT, lang, stats }) {
  const S = SECTION_TEXT[lang] || SECTION_TEXT.tr;
  const items = [
    {
      icon: <BusinessIcon />,
      label: TT.totalRecords,
      value: stats.totalRecords,
      bg: BRAND.softBlue,
      color: BRAND.blue,
    },
    {
      icon: <FactCheckIcon />,
      label: TT.activeContracts,
      value: stats.activeContracts,
      bg: BRAND.softGreen,
      color: "#047857",
    },
   {
  icon: <EventRepeatIcon />,
  label: TT.upcomingRenewals,
  value: stats.upcomingRenewals,
  bg: BRAND.softYellow,
  color: "#B45309",
},
{
  icon: <PointOfSaleIcon />,
  label: stats.currentMonthSalesLabel,
  value: stats.currentMonthSalesCount,
  bg: "#F0FDF4",
  color: "#15803D",
},
{
  icon: <ReceiptLongIcon />,
  label: TT.totalSalesAmount,
  value: moneyTextShort(stats.totalSales, "TRY", lang),
  bg: BRAND.softOrange,
  color: "#9A3412",
  wideMobile: true,
},
{
  icon: <HandshakeIcon />,
  label: TT.infoCommission,
  value: moneyTextShort(stats.totalCommission, "TRY", lang),
  bg: "#EEF2FF",
  color: "#3730A3",
  wideMobile: true,
},
  ];

  return (
    <Card
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        borderRadius: { xs: 3, md: 4 },
        bgcolor: "#fff",
        boxShadow: "0 16px 40px rgba(15,23,42,.075)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.4, md: 2 },
          py: { xs: 1.35, md: 1.65 },
          color: "#fff",
          background:
            "linear-gradient(135deg, #0F172A 0%, #0B4D8B 66%, #1565C0 100%)",
          borderBottom: `5px solid ${BRAND.orange}`,
        }}
      >
        <Typography sx={{ fontSize: { xs: 19, md: 24 }, fontWeight: 950, lineHeight: 1.15 }}>
          {S.overviewTableTitle}
        </Typography>
        <Typography sx={{ mt: 0.4, color: "rgba(255,255,255,.82)", fontSize: { xs: 12, md: 13 }, fontWeight: 650 }}>
          {S.overviewTableSubtitle}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 0.8, md: 1.15 } }}>
        {items.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              minWidth: 0,
              display: "grid",
              gridTemplateColumns: {
                xs: "40px minmax(0, 1fr) minmax(86px, auto)",
                md: "48px minmax(0, 1fr) minmax(180px, .32fr)",
              },
              gap: { xs: 0.8, md: 1.1 },
              alignItems: "center",
              px: { xs: 0.8, md: 1.2 },
              py: { xs: 0.9, md: 1.05 },
              borderBottom: index === items.length - 1 ? "none" : `1px solid ${BRAND.border}`,
              borderRadius: 1.8,
              bgcolor: index % 2 === 0 ? "#F8FAFC" : "#fff",
            }}
          >
            <Avatar
              sx={{
                width: { xs: 36, md: 40 },
                height: { xs: 36, md: 40 },
                bgcolor: item.bg,
                color: item.color,
                border: `1px solid ${item.color}22`,
                "& svg": { fontSize: { xs: 19, md: 21 } },
              }}
            >
              {item.icon}
            </Avatar>

            <Typography
              sx={{
                minWidth: 0,
                color: BRAND.ink,
                fontSize: { xs: 12.3, md: 13.5 },
                fontWeight: 850,
                lineHeight: 1.35,
                overflowWrap: "anywhere",
              }}
            >
              {item.label}
            </Typography>

            <Typography
              sx={{
                minWidth: 0,
                color: item.color,
                fontSize: { xs: 16, md: 19 },
                fontWeight: 950,
                lineHeight: 1.15,
                textAlign: "right",
                overflowWrap: "anywhere",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

function EducationDevelopmentPanel({ lang }) {
  const S = SECTION_TEXT[lang] || SECTION_TEXT.tr;

  return (
    <Card
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        borderRadius: { xs: 3, md: 4 },
        bgcolor: "#fff",
        boxShadow: "0 18px 46px rgba(15,23,42,.08)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.4, md: 2.1 },
          py: { xs: 1.35, md: 1.7 },
          color: "#fff",
          background:
            "linear-gradient(135deg, #0F172A 0%, #0B4D8B 68%, #F58220 145%)",
          borderBottom: `5px solid ${BRAND.orange}`,
        }}
      >
        <Typography sx={{ fontSize: { xs: 19, md: 25 }, fontWeight: 950, lineHeight: 1.15 }}>
          {S.educationTitle}
        </Typography>
        <Typography sx={{ mt: 0.45, color: "rgba(255,255,255,.82)", fontSize: { xs: 12, md: 13.2 }, fontWeight: 650, lineHeight: 1.55 }}>
          {S.educationSubtitle}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 0.8, sm: 1, md: 1.25 }, bgcolor: "#F4F7FB" }}>
        <AcademyProjectsAccordion lang={lang} />
        <BusinessGrowthAccordion lang={lang} />
        <FrequentlyAskedQuestionsAccordion lang={lang} />
      </Box>
    </Card>
  );
}

function AcademyProjectsAccordion({ lang }) {
  const A = ACADEMY_TEXT[lang] || ACADEMY_TEXT.tr;

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        borderRadius: { xs: 3, md: 4 },
        overflow: "hidden",
        border: "1px solid rgba(15,23,42,.14)",
        boxShadow: "0 18px 46px rgba(15,23,42,.09)",
        bgcolor: "#fff",
        "&:before": { display: "none" },
        "&.Mui-expanded": { m: 0, mb: { xs: 1.2, md: 1.5 } },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ color: "#fff" }} />}
        sx={{
          minHeight: { xs: 88, md: 104 },
          px: { xs: 1.5, sm: 2, md: 2.6 },
          py: { xs: 1.2, md: 1.5 },
          color: "#fff",
          background:
            "linear-gradient(135deg, #0F172A 0%, #0B4D8B 58%, #1565C0 100%)",
          "&.Mui-expanded": { minHeight: { xs: 88, md: 104 } },
          "& .MuiAccordionSummary-content": {
            minWidth: 0,
            my: 0,
            "&.Mui-expanded": { my: 0 },
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            width: 40,
            height: 40,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            bgcolor: "rgba(255,255,255,.13)",
            flexShrink: 0,
          },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1.6 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ width: "100%", minWidth: 0, pr: 1 }}
        >
          <Avatar
            sx={{
              width: { xs: 46, md: 56 },
              height: { xs: 46, md: 56 },
              color: "#0F172A",
              bgcolor: "#fff",
              boxShadow: "0 9px 22px rgba(0,0,0,.18)",
              flexShrink: 0,
            }}
          >
            <FactCheckIcon />
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: "#FBBF24",
                fontSize: { xs: 10, md: 11 },
                fontWeight: 950,
                letterSpacing: ".13em",
              }}
            >
              {A.kicker}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                color: "#fff",
                fontSize: { xs: 20, sm: 23, md: 28 },
                fontWeight: 950,
                lineHeight: 1.12,
                wordBreak: "break-word",
              }}
            >
              {A.title}
            </Typography>
            <Typography
              sx={{
                mt: 0.55,
                maxWidth: 920,
                color: "rgba(255,255,255,.82)",
                fontSize: { xs: 12, md: 13.5 },
                fontWeight: 650,
                lineHeight: 1.55,
              }}
            >
              {A.subtitle}
            </Typography>
          </Box>

        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: { xs: 1, sm: 1.3, md: 1.7 },
          bgcolor: "#F4F7FB",
        }}
      >
        <AcademyModule
          icon="01"
          title={A.academyTitle}
          subtitle={A.academySubtitle}
          accent={BRAND.blue}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 1,
            }}
          >
            {A.trainingSteps.map((step) => (
              <Box
                key={step.title}
                sx={{
                  minWidth: 0,
                  p: { xs: 1.25, md: 1.45 },
                  borderRadius: 2.5,
                  border: "1px solid #DCE5EE",
                  bgcolor: "#fff",
                }}
              >
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: 14 }}>
                  {step.title}
                </Typography>
                <Typography
                  sx={{ mt: 0.55, color: BRAND.muted, fontWeight: 650, fontSize: 12.5, lineHeight: 1.6 }}
                >
                  {step.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </AcademyModule>

      </AccordionDetails>
    </Accordion>
  );
}

function ProjectPricingTableSection({ lang }) {
  const A = ACADEMY_TEXT[lang] || ACADEMY_TEXT.tr;

  return (
    <PricingStaticSection
      icon="₺"
      title={A.projectsTitle}
      subtitle={A.projectsSubtitle}
      accent={BRAND.orange}
    >
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "minmax(270px, .9fr) minmax(0, 1.7fr)",
          gap: 0,
          px: 1.4,
          py: 0.9,
          mb: 0.7,
          borderRadius: 2.2,
          color: "#fff",
          bgcolor: BRAND.dark,
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ pr: 2 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 950, letterSpacing: ".08em" }}>
            {A.projectColumn.toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: 10.5, fontWeight: 950, letterSpacing: ".08em" }}>
            {A.priceColumn.toUpperCase()}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 10.5, fontWeight: 950, letterSpacing: ".08em" }}>
          {A.scopeColumn.toUpperCase()}
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 0.75 }}>
        {PROJECT_CATALOG.map((project) => (
          <ProjectPackageAccordion
            key={project.key}
            project={project}
            lang={lang}
            A={A}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: 1.2,
          p: { xs: 1.2, md: 1.45 },
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          border: "1px solid #F3C68E",
          borderRadius: 2.5,
          color: "#78350F",
          bgcolor: "#FFF8EB",
        }}
      >
        <Box
          sx={{
            width: 23,
            height: 23,
            flex: "0 0 23px",
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            color: "#fff",
            bgcolor: "#B45309",
            fontSize: 12,
            fontWeight: 950,
          }}
        >
          i
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 750, lineHeight: 1.55 }}>
          {A.priceNote}
        </Typography>
      </Box>
    </PricingStaticSection>
  );
}

function BusinessGrowthAccordion({ lang }) {
  const A = ACADEMY_TEXT[lang] || ACADEMY_TEXT.tr;

  return (
    <CorporateSectionAccordion
      number="02"
      sectionLabel={A.sectionLabel}
      title={A.salesTitle}
      subtitle={A.salesSubtitle}
      accent="#0F766E"
      soft="#ECFDF5"
      icon={<PointOfSaleIcon />}
    >
      <Box
        component="ol"
        sx={{
          m: 0,
          p: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
          gap: 0.9,
          listStyle: "none",
        }}
      >
        {A.salesSteps.map((step, index) => (
          <Box
            component="li"
            key={step}
            sx={{
              minWidth: 0,
              p: 1.25,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              border: "1px solid #CFE3DE",
              borderRadius: 2.5,
              bgcolor: "#fff",
            }}
          >
            <Avatar
              sx={{
                width: 29,
                height: 29,
                bgcolor: "#0F766E",
                fontSize: 12,
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              {index + 1}
            </Avatar>
            <Typography sx={{ color: BRAND.ink, fontSize: 12.5, fontWeight: 750, lineHeight: 1.6 }}>
              {step}
            </Typography>
          </Box>
        ))}
      </Box>
    </CorporateSectionAccordion>
  );
}

function FrequentlyAskedQuestionsAccordion({ lang }) {
  const A = ACADEMY_TEXT[lang] || ACADEMY_TEXT.tr;

  return (
    <CorporateSectionAccordion
      number="03"
      sectionLabel={A.sectionLabel}
      title={A.faqTitle}
      subtitle={A.faqSubtitle}
      accent="#6D3BA8"
      soft="#F6F1FA"
      icon={<LanguageIcon />}
    >
      <Stack spacing={0.75}>
        {A.faqs.map((faq, index) => (
          <Accordion
            key={faq.question}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #DDD4E8",
              borderRadius: "12px !important",
              overflow: "hidden",
              bgcolor: "#fff",
              "&:before": { display: "none" },
              "&.Mui-expanded": { m: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowDownIcon sx={{ color: "#4C1D71" }} />}
              sx={{
                minHeight: 50,
                px: 1.3,
                bgcolor: "#fff",
                "&.Mui-expanded": { minHeight: 50 },
                "& .MuiAccordionSummary-content": { my: 1, minWidth: 0 },
                "& .MuiAccordionSummary-content.Mui-expanded": { my: 1 },
              }}
            >
              <Typography sx={{ color: "#3B1D5A", fontSize: 13, fontWeight: 900, lineHeight: 1.4 }}>
                {index + 1}. {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.4, pt: 0, pb: 1.4, bgcolor: "#FBF9FD" }}>
              <Typography sx={{ color: BRAND.muted, fontSize: 12.5, fontWeight: 650, lineHeight: 1.65 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </CorporateSectionAccordion>
  );
}

function CorporateSectionAccordion({ number, sectionLabel, title, subtitle, accent, soft, icon, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: { xs: 1.2, md: 1.5 },
        border: "1px solid #D9E2EC",
        borderLeft: `6px solid ${accent}`,
        borderRadius: { xs: "14px !important", md: "16px !important" },
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 12px 30px rgba(15,23,42,.065)",
        "&:before": { display: "none" },
        "&.Mui-expanded": { m: 0, mb: { xs: 1.2, md: 1.5 } },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ color: accent }} />}
        sx={{
          minHeight: { xs: 76, md: 84 },
          px: { xs: 1.2, md: 1.7 },
          bgcolor: "#fff",
          "&.Mui-expanded": { minHeight: { xs: 76, md: 84 } },
          "& .MuiAccordionSummary-content": { my: 1.1, minWidth: 0 },
          "& .MuiAccordionSummary-content.Mui-expanded": { my: 1.1 },
          "& .MuiAccordionSummary-expandIconWrapper": {
            width: 38,
            height: 38,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.3,
            bgcolor: soft,
          },
        }}
      >
        <Stack direction="row" spacing={1.15} alignItems="center" sx={{ width: "100%", minWidth: 0, pr: 1 }}>
          <Avatar sx={{ width: 42, height: 42, color: "#fff", bgcolor: accent, flexShrink: 0 }}>
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: accent, fontSize: 9.5, fontWeight: 950, letterSpacing: ".12em" }}>
              {sectionLabel} {number}
            </Typography>
            <Typography sx={{ mt: 0.25, color: BRAND.dark, fontSize: { xs: 16, md: 19 }, fontWeight: 950, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 0.25, color: BRAND.muted, fontSize: 11.5, fontWeight: 650, lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1, md: 1.4 }, pt: 0, bgcolor: soft }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function PricingStaticSection({ icon, title, subtitle, accent, children }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid #D9E2EC",
        borderTop: `5px solid ${accent}`,
        borderRadius: { xs: 3, md: 3.5 },
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 12px 30px rgba(15,23,42,.065)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.3, md: 1.7 },
          py: { xs: 1.35, md: 1.55 },
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          borderBottom: "1px solid #E2E8F0",
          bgcolor: "#fff",
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            color: "#fff",
            bgcolor: accent,
            fontSize: 12,
            fontWeight: 950,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: BRAND.dark, fontSize: { xs: 15.5, md: 18 }, fontWeight: 950, lineHeight: 1.25 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.3, color: BRAND.muted, fontSize: 12, fontWeight: 650, lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: { xs: 1, md: 1.35 }, bgcolor: "#F8FAFC" }}>{children}</Box>
    </Card>
  );
}

function AcademyModule({ icon, title, subtitle, accent, defaultExpanded = false, children }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid #D9E2EC",
        borderLeft: `5px solid ${accent}`,
        borderRadius: "14px !important",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 7px 20px rgba(15,23,42,.045)",
        "&:before": { display: "none" },
        "&.Mui-expanded": { m: 0, mb: 1 },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ color: accent }} />}
        sx={{
          minHeight: { xs: 66, md: 72 },
          px: { xs: 1.2, md: 1.6 },
          bgcolor: "#fff",
          "&.Mui-expanded": { minHeight: { xs: 66, md: 72 } },
          "& .MuiAccordionSummary-content": { my: 1.1, minWidth: 0 },
          "& .MuiAccordionSummary-content.Mui-expanded": { my: 1.1 },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              color: "#fff",
              bgcolor: accent,
              fontSize: 12,
              fontWeight: 950,
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND.dark, fontSize: { xs: 14.5, md: 16 }, fontWeight: 950, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 0.25, color: BRAND.muted, fontSize: 11.5, fontWeight: 650, lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1, md: 1.4 }, pt: 0, bgcolor: "#F8FAFC" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function ProjectPackageAccordion({ project, lang, A }) {
  const featureLabel = (key) =>
    FEATURE_TEXT[key]?.[lang] || FEATURE_TEXT[key]?.tr || key;

  return (
    <Card
      elevation={0}
      sx={{
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "minmax(270px, .9fr) minmax(0, 1.7fr)",
        },
        border: project.popular
          ? `2px solid ${BRAND.orange}`
          : "1px solid #D9E2EC",
        borderRadius: 2.7,
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: project.popular
          ? "0 11px 27px rgba(245,130,32,.14)"
          : "0 6px 18px rgba(15,23,42,.045)",
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          p: { xs: 1.25, md: 1.4 },
          bgcolor: project.popular ? "#FFF9F2" : "#fff",
        }}
      >
        <Box sx={{ width: "100%", minWidth: 0, pr: 0.7 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Avatar
              sx={{
                width: 39,
                height: 39,
                color: project.popular ? "#fff" : BRAND.blue,
                bgcolor: project.popular ? BRAND.orange : BRAND.softBlue,
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              {project.icon}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                <Chip
                  label={project.category[lang] || project.category.tr}
                  size="small"
                  sx={{ height: 22, color: "#9A3412", bgcolor: BRAND.softOrange, fontSize: 9.5, fontWeight: 900 }}
                />
                {project.popular && (
                  <Chip
                    label={A.mostPreferred}
                    size="small"
                    sx={{ height: 22, color: "#fff", bgcolor: BRAND.orange, fontSize: 9.5, fontWeight: 900 }}
                  />
                )}
              </Stack>
              <Typography sx={{ mt: 0.65, color: BRAND.dark, fontSize: 14, fontWeight: 950, lineHeight: 1.3 }}>
                {project.title[lang] || project.title.tr}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.6} alignItems="baseline" sx={{ mt: 1 }}>
            <Typography sx={{ color: BRAND.blue2, fontSize: 20, fontWeight: 950, lineHeight: 1 }}>
              {project.price}
            </Typography>
            <Typography sx={{ color: BRAND.muted, fontSize: 10.5, fontWeight: 800 }}>
              {A.perYear}
            </Typography>
            {project.starting && (
              <Typography sx={{ color: "#9A3412", fontSize: 9.5, fontWeight: 900 }}>
                · {A.startingFrom}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          minWidth: 0,
          p: { xs: 1.25, md: 1.4 },
          borderTop: { xs: "1px solid #E2E8F0", md: 0 },
          borderLeft: { xs: 0, md: "1px solid #E2E8F0" },
          bgcolor: project.popular ? "#FFFCF8" : "#FBFCFE",
        }}
      >
        <Typography sx={{ color: BRAND.muted, fontSize: 11.5, fontWeight: 650, lineHeight: 1.6 }}>
          {project.description[lang] || project.description.tr}
        </Typography>

        <Typography sx={{ mt: 1.2, color: BRAND.dark, fontSize: 10.5, fontWeight: 950, letterSpacing: ".06em" }}>
          {A.includedFeatures.toUpperCase()}
        </Typography>

        <Box
          sx={{
            mt: 0.8,
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 0.65,
          }}
        >
          {project.features.map((feature) => (
            <Stack key={feature} direction="row" spacing={0.75} alignItems="flex-start">
              <Box
                sx={{
                  width: 17,
                  height: 17,
                  flex: "0 0 17px",
                  mt: "1px",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  color: "#fff",
                  bgcolor: "#0F766E",
                  fontSize: 10,
                  fontWeight: 950,
                }}
              >
                ✓
              </Box>
              <Typography sx={{ color: BRAND.ink, fontSize: 11.5, fontWeight: 750, lineHeight: 1.45 }}>
                {featureLabel(feature)}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Card>
  );
}

function ReportsAccordion({ TT, lang, records, reportData, title, subtitle }) {
  const totalSales = records.reduce(
    (sum, row) => sum + parseNumber(row.satis_tutari),
    0
  );

  const totalCommission = records.reduce(
    (sum, row) => sum + getCommissionAmount(row),
    0
  );

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        mb: {
          xs: 1.2,
          md: 1.5,
        },
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 36px rgba(15,23,42,.06)",
        bgcolor: "#fff",
        "&:before": {
          display: "none",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          width: 42,
          height: 42,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.softBlue,
          color: BRAND.blue,
          transition: "all .25s ease",
        },
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
          transform: "rotate(180deg) scale(1.08)",
          bgcolor: BRAND.softOrange,
          color: "#9A3412",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 34 }} />}
        sx={{
          px: {
            xs: 1.3,
            md: 1.8,
          },
          py: {
            xs: 0.6,
            md: 0.8,
          },
          minHeight: "unset",
          "& .MuiAccordionSummary-content": {
            my: 0.7,
            minWidth: 0,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.1}
          alignItems="center"
          sx={{ minWidth: 0, width: "100%" }}
        >
          <Avatar
            sx={{
              bgcolor: BRAND.softOrange,
              color: "#9A3412",
              width: 42,
              height: 42,
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon />
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 950,
                color: BRAND.dark,
                fontSize: {
                  xs: 20,
                  md: 22,
                },
                lineHeight: 1.1,
                wordBreak: "break-word",
              }}
            >
              {title || TT.reportsTitle}
            </Typography>

            <Typography
              sx={{
                color: BRAND.muted,
                fontWeight: 750,
                fontSize: 13,
                mt: 0.25,
                wordBreak: "break-word",
              }}
            >
              {subtitle || TT.reportsSubtitle}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.7}
            sx={{
              display: {
                xs: "none",
                md: "flex",
              },
            }}
          >
            <Chip
              size="small"
              label={`${records.length} ${TT.rows}`}
              sx={{
                bgcolor: BRAND.softBlue,
                color: BRAND.blue,
                fontWeight: 950,
              }}
            />

            <Chip
              size="small"
              label={moneyText(totalSales, "TRY", lang)}
              sx={{
                bgcolor: BRAND.softOrange,
                color: "#9A3412",
                fontWeight: 950,
              }}
            />

            <Chip
              size="small"
              label={moneyText(totalCommission, "TRY", lang)}
              sx={{
                bgcolor: "#EEF2FF",
                color: "#3730A3",
                fontWeight: 950,
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: {
            xs: 1.1,
            md: 1.4,
          },
          pt: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
            },
            gap: {
              xs: 1.1,
              md: 1.3,
            },
            minWidth: 0,
          }}
        >
          <ReportTable
            title={TT.customerReport}
            subtitle={TT.customerReportSub}
            emptyText={TT.noReport}
            rows={reportData.byCustomer}
            lang={lang}
            mode="customer"
            TT={TT}
          />

          <ReportTable
            title={TT.productReport}
            subtitle={TT.productReportSub}
            emptyText={TT.noReport}
            rows={reportData.byProduct}
            lang={lang}
            mode="product"
            TT={TT}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function ReportTable({ title, subtitle, emptyText, rows, lang, mode, TT }) {
  const palette = [
    {
      bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
      border: "#93C5FD",
      color: "#1D4ED8",
    },
    {
      bg: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)",
      border: "#FDBA74",
      color: "#C2410C",
    },
    {
      bg: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
      border: "#6EE7B7",
      color: "#047857",
    },
    {
      bg: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
      border: "#C4B5FD",
      color: "#6D28D9",
    },
    {
      bg: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
      border: "#FCA5A5",
      color: "#B91C1C",
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3.5,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        overflow: "hidden",
        minWidth: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          p: {
            xs: 1.2,
            md: 1.4,
          },
          background:
            mode === "customer"
              ? "linear-gradient(135deg, #0B4D8B 0%, #1565C0 100%)"
              : "linear-gradient(135deg, #F58220 0%, #FB923C 100%)",
          color: "#fff",
        }}
      >
        <Typography
          sx={{
            fontWeight: 950,
            fontSize: {
              xs: 17,
              md: 18,
            },
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            color: "rgba(255,255,255,.84)",
            fontWeight: 700,
            fontSize: 12.5,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          p: {
            xs: 1,
            md: 1.2,
          },
        }}
      >
        <Box
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1.2fr) 80px 140px 140px",
            },
            gap: 0.8,
            px: 0.7,
            py: 0.6,
            color: BRAND.muted,
            fontWeight: 950,
            fontSize: 11.5,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            display: {
              xs: "none",
              md: "grid",
            },
          }}
        >
          <Box>{mode === "customer" ? TT.customerAccount : TT.packageName}</Box>
          <Box textAlign="center">{TT.quantity}</Box>
          <Box textAlign="right">{TT.saleAmount}</Box>
          <Box textAlign="right">{TT.commissionAmount}</Box>
        </Box>

        {!rows?.length ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: "#F8FAFC",
              border: `1px dashed ${BRAND.border2}`,
              color: BRAND.muted,
              fontWeight: 800,
              textAlign: "center",
            }}
          >
            {emptyText}
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {rows.slice(0, 8).map((row, index) => {
              const c = palette[index % palette.length];

              return (
                <Box
                  key={`${row.key}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(0, 1.2fr) 80px 140px 140px",
                    },
                    gap: {
                      xs: 0.55,
                      md: 0.8,
                    },
                    alignItems: "center",
                    p: {
                      xs: 1,
                      md: 0.9,
                    },
                    borderRadius: 3,
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: "#fff",
                        color: c.color,
                        fontWeight: 950,
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: BRAND.dark,
                          fontWeight: 950,
                          lineHeight: 1.15,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.label}
                      </Typography>

                      {row.subText && row.subText !== "-" && (
                        <Typography
                          sx={{
                            color: BRAND.muted,
                            fontWeight: 750,
                            fontSize: 12,
                            mt: 0.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.subText}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <MetricPill label={TT.quantity} value={row.count} color={c.color} />
                  <MetricPill
                    label={TT.saleAmount}
                    value={moneyText(row.saleAmount, "TRY", lang)}
                    color={c.color}
                    right
                  />
                  <MetricPill
                    label={TT.commissionAmount}
                    value={moneyText(row.commissionAmount, "TRY", lang)}
                    color={c.color}
                    right
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

function MetricPill({ label, value, color, right }) {
  return (
    <Box
      sx={{
        textAlign: {
          xs: "left",
          md: right ? "right" : "center",
        },
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          display: {
            xs: "block",
            md: "none",
          },
          color: BRAND.muted,
          fontWeight: 850,
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color,
          fontWeight: 950,
          fontSize: {
            xs: 13,
            md: 13.5,
          },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SalesAccordion({ TT, total, children }) {
  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 36px rgba(15,23,42,.06)",
        bgcolor: "#fff",
        "&:before": {
          display: "none",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          width: 42,
          height: 42,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.softOrange,
          color: "#9A3412",
          transition: "all .25s ease",
        },
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
          transform: "rotate(180deg) scale(1.08)",
          bgcolor: BRAND.softBlue,
          color: BRAND.blue,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 34 }} />}
        sx={{
          px: {
            xs: 1.4,
            md: 1.8,
          },
          py: {
            xs: 0.6,
            md: 0.8,
          },
          minHeight: "unset",
          borderBottom: `1px solid ${BRAND.border}`,
          background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
          "& .MuiAccordionSummary-content": {
            my: 0.7,
            minWidth: 0,
          },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              bgcolor: BRAND.softBlue,
              color: BRAND.blue,
              width: 42,
              height: 42,
              flexShrink: 0,
            }}
          >
            <HandshakeIcon />
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 950,
                color: BRAND.dark,
                fontSize: {
                  xs: 20,
                  md: 22,
                },
                lineHeight: 1.1,
              }}
            >
              {TT.sales}
            </Typography>

            <Typography
              sx={{
                color: BRAND.muted,
                fontWeight: 750,
                fontSize: 13,
                mt: 0.25,
              }}
            >
              {total} {TT.salesListed}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: {
            xs: 1,
            md: 1.3,
          },
          bgcolor: "#F8FAFC",
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function RecordsHeader({ TT, search, setSearch, total, cardMode }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: {
          xs: 1.1,
          md: 1.3,
        },
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        boxShadow: "0 12px 30px rgba(15,23,42,.05)",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.3,
            md: 1.6,
          },
          "&:last-child": {
            pb: {
              xs: 1.3,
              md: 1.6,
            },
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1.2}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography
                sx={{
                  fontSize: {
                    xs: 18,
                    md: 21,
                  },
                  fontWeight: 950,
                  color: BRAND.dark,
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                }}
              >
                {TT.recordsTitle}
              </Typography>

              <Chip
                size="small"
                label={`${total} ${TT.rows}`}
                sx={{
                  bgcolor: BRAND.softBlue,
                  color: BRAND.blue,
                  fontWeight: 950,
                }}
              />
            </Stack>

            <Typography
              sx={{
                mt: 0.5,
                color: BRAND.muted,
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {TT.recordsSubtitle}
            </Typography>
          </Box>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={TT.searchPlaceholder}
            size="small"
            fullWidth={cardMode}
            sx={{
              ...fieldSx,
              width: {
                xs: "100%",
                md: 390,
              },
              flexShrink: 0,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyState({ TT }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px dashed ${BRAND.border2}`,
        bgcolor: "#fff",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 3,
            md: 5,
          },
          textAlign: "center",
        }}
      >
        <Avatar
          sx={{
            width: 66,
            height: 66,
            mx: "auto",
            mb: 1.5,
            bgcolor: BRAND.softBlue,
            color: BRAND.blue,
          }}
        >
          <BusinessIcon />
        </Avatar>

        <Typography
          sx={{
            fontWeight: 950,
            fontSize: {
              xs: 20,
              md: 24,
            },
            color: BRAND.dark,
          }}
        >
          {TT.noData}
        </Typography>

        <Typography
          sx={{
            color: BRAND.muted,
            fontWeight: 700,
            mt: 0.7,
            lineHeight: 1.5,
          }}
        >
          {TT.noDataText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function RecordCard({ row, TT, lang }) {
  const chip = getRemainingChip(row.sozlesme_bitis, TT);
  const commissionAmount = getCommissionAmount(row);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        boxShadow: "0 12px 32px rgba(15,23,42,.06)",
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.25,
            sm: 1.6,
            md: 1.8,
          },
          "&:last-child": {
            pb: {
              xs: 1.25,
              sm: 1.6,
              md: 1.8,
            },
          },
          minWidth: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={{
            xs: 1.2,
            md: 1.6,
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "flex-start",
          }}
          sx={{
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(BRAND.blue, 0.08),
                color: BRAND.blue,
                width: {
                  xs: 44,
                  md: 52,
                },
                height: {
                  xs: 44,
                  md: 52,
                },
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              {safeText(row.firma_adi, "?").charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  mb: 0.4,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 950,
                    color: BRAND.dark,
                    fontSize: {
                      xs: 17,
                      md: 20,
                    },
                    lineHeight: 1.18,
                    wordBreak: "break-word",
                    minWidth: 0,
                  }}
                >
                  {safeText(row.firma_adi, TT.company)}
                </Typography>

                <Chip
                  size="small"
                  label={getProcessTypeText(row.islem_tipi, TT)}
                  sx={{
                    bgcolor: BRAND.softBlue,
                    color: BRAND.blue,
                    fontWeight: 950,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />

                <Chip
                  size="small"
                  label={chip.label}
                  sx={{
                    bgcolor: chip.bg,
                    color: chip.color,
                    fontWeight: 950,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: {
                    xs: 13,
                    md: 14,
                  },
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {TT.partner}: <b>{safeText(row.referans)}</b>
              </Typography>

              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: {
                    xs: 13,
                    md: 14,
                  },
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {TT.packageName}: <b>{safeText(row.satilan_urun)}</b>
              </Typography>

              {(row.musteri_email || row.musteri_code || row.musteri_adi) && (
                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontWeight: 750,
                    fontSize: {
                      xs: 13,
                      md: 14,
                    },
                    lineHeight: 1.45,
                    wordBreak: "break-word",
                  }}
                >
                  {TT.customerAccount}:{" "}
                  <b>
                    {safeText(row.musteri_adi || row.firma_adi)}
                    {row.musteri_email ? ` · ${row.musteri_email}` : ""}
                    {row.musteri_code ? ` · Kod: ${row.musteri_code}` : ""}
                  </b>
                </Typography>
              )}
            </Box>
          </Stack>

          <Box
            sx={{
              width: {
                xs: "100%",
                md: 330,
              },
              minWidth: 0,
              p: {
                xs: 1.1,
                md: 1.3,
              },
              borderRadius: 3,
              bgcolor: "#F8FAFC",
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <Stack spacing={0.8}>
              <InfoLine
                label={TT.saleAmount}
                value={moneyText(row.satis_tutari, row.currency, lang)}
              />

              <InfoLine
                label={`${row.hakedis_kademe || "1"}. ${TT.commissionStep}`}
                value={percentText(row.hakedis_orani, lang)}
              />

              <InfoLine
                label={TT.commissionAmount}
                value={moneyText(commissionAmount, row.currency, lang)}
                strong
              />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: { xs: 1.2, md: 1.4 } }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: {
              xs: 0.9,
              md: 1,
            },
            minWidth: 0,
          }}
        >
          <MiniBox
            label={TT.agreementDate}
            value={dateText(row.anlasma_tarihi, lang)}
          />

          <MiniBox
            label={TT.startDate}
            value={dateText(row.sozlesme_baslangic, lang)}
          />

          <MiniBox
            label={TT.endDate}
            value={dateText(row.sozlesme_bitis, lang)}
          />

          <MiniBox label={TT.remainingDay} value={chip.label} />
        </Box>

        <Box
          sx={{
            mt: 1.2,
            p: 1.2,
            borderRadius: 3,
            bgcolor: row.anlasma_notu ? BRAND.softOrange : "#F8FAFC",
            border: row.anlasma_notu
              ? "1px dashed rgba(245,130,32,.35)"
              : `1px dashed ${BRAND.border2}`,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 950,
              color: row.anlasma_notu ? "#9A3412" : BRAND.muted,
              mb: 0.3,
            }}
          >
            {TT.note}
          </Typography>

          <Typography
            sx={{
              color: BRAND.ink,
              fontWeight: 700,
              fontSize: {
                xs: 12.5,
                md: 13.5,
              },
              lineHeight: 1.55,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {safeText(row.anlasma_notu, TT.noNote)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function InfoLine({ label, value, strong = false }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: BRAND.muted,
          fontWeight: 800,
          fontSize: 12,
          minWidth: 0,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: strong ? BRAND.blue : BRAND.dark,
          fontWeight: 950,
          fontSize: {
            xs: 12.5,
            md: 13.5,
          },
          textAlign: "right",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "58%",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function MiniBox({ label, value }) {
  return (
    <Box
      sx={{
        p: {
          xs: 1,
          md: 1.1,
        },
        borderRadius: 3,
        bgcolor: "#F8FAFC",
        border: `1px solid ${BRAND.border}`,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: BRAND.muted,
          fontWeight: 850,
          fontSize: 12,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: BRAND.dark,
          fontWeight: 950,
          mt: 0.35,
          fontSize: {
            xs: 13,
            md: 14,
          },
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
